import { useState, useEffect, useCallback } from "react";
import CitadelAnalysisPage from "./pages/Mar2026adhoc";
import Oct2026CaseStudy from "./pages/Oct2026CaseStudy";
import ThemeShowcase from "./pages/ThemeShowcase";
import PerformanceIntelligenceChart from "./components/PerformanceIntelligenceChart";
import { SUPABASE_URL, SUPABASE_KEY, ACCESS_PASSWORD, APAC_LOCATIONS, groupLoc, isRelevantNews } from "./config";

// ─── Supabase helper ─────────────────────────────────────────
async function sbQuery(table, opts = {}, schema = 'public') {
  let url = `${SUPABASE_URL}/rest/v1/${table}?`;
  const params = [];
  if (opts.select) params.push(`select=${opts.select}`);
  if (opts.order) params.push(`order=${opts.order}`);
  if (opts.limit) params.push(`limit=${opts.limit}`);
  if (opts.filter) params.push(opts.filter);
  url += params.join('&');
  const headers = { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` };
  if (schema !== 'public') headers['Accept-Profile'] = schema;
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error(`Supabase error: ${res.status}`);
  return res.json();
}
async function sbQueryOptional(table, opts = {}, schema = 'public') {
  try { return await sbQuery(table, opts, schema); }
  catch { return []; }
}
async function sbInsert(table, data) {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
    method: 'POST', headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}`, 'Content-Type': 'application/json', Prefer: 'return=minimal' },
    body: JSON.stringify(data),
  });
  return res.ok;
}

// ─── Helpers ─────────────────────────────────────────────────
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US',{month:'short',day:'numeric'}) : '—';
const fmtDateFull = (d) => d ? new Date(d).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—';
const fmtDateDMY = (d) => { if (!d) return '—'; const dt = new Date(d); const day = String(dt.getDate()).padStart(2,'0'); const mon = dt.toLocaleDateString('en-US',{month:'short'}).toUpperCase(); const yr = dt.getFullYear(); return `${day}/${mon}/${yr}`; };
const TRADING_REVENUE_METRICS = new Set(['net_trading_income', 'revenue', 'net_trading_revenue', 'trading_revenue']);
const FINANCIAL_VALUE_KINDS = new Set(['point', 'range', 'lower_bound', 'upper_bound']);
const RAW_FINANCIAL_VALUE_KINDS = new Set(['point', 'range', 'lower_bound', 'upper_bound']);
const FINANCIAL_UNIT_SCALE = { unit: 1, units: 1, whole: 1, thousand: 1e3, million: 1e6, billion: 1e9 };
const FINANCIAL_CONFIDENCE_RANK = { confirmed: 6, reported: 5, estimated: 4, contested: 3, rumoured: 2, insufficient: 1, insufficient_evidence: 1 };
const FINANCIAL_CONFIDENCE_META = {
  confirmed: { bg:'rgba(0,135,83,0.1)', color:'#008753', icon:'●', label:'Confirmed' },
  reported: { bg:'rgba(60,91,124,0.12)', color:'#3c5b7c', icon:'◐', label:'Reported' },
  estimated: { bg:'rgba(1,23,53,0.1)', color:'#011735', icon:'○', label:'Estimated' },
  contested: { bg:'rgba(127,0,255,0.1)', color:'#7f00ff', icon:'↔', label:'Contested' },
  rumoured: { bg:'rgba(255,51,0,0.1)', color:'#cc2900', icon:'○', label:'Rumoured' },
  insufficient: { bg:'rgba(94,126,160,0.1)', color:'#3c5b7c', icon:'—', label:'Insufficient evidence' },
  insufficient_evidence: { bg:'rgba(94,126,160,0.1)', color:'#3c5b7c', icon:'—', label:'Insufficient evidence' },
};
const compactNumber = (value) => {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const compact = (amount, suffix) => `${Number(amount.toFixed(1))}${suffix}`;
  if (abs >= 1e9) return `${sign}${compact(abs / 1e9, 'B')}`;
  if (abs >= 1e6) return `${sign}${compact(abs / 1e6, 'M')}`;
  if (abs >= 1e3) return `${sign}${compact(abs / 1e3, 'K')}`;
  return `${value}`;
};
const formatMoney = (value, currency) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  const normalizedCurrency = (currency || '').toUpperCase();
  const symbols = { USD: '$', EUR: '€', GBP: '£', AUD: 'A$', CAD: 'C$', JPY: '¥', CHF: 'CHF ' };
  const prefix = symbols[normalizedCurrency] || (normalizedCurrency ? `${normalizedCurrency} ` : '');
  return `${prefix}${compactNumber(numeric)}`;
};
const finiteValue = (...values) => values.map(value => value === null || value === undefined || value === '' ? Number.NaN : Number(value)).find(Number.isFinite);
const financialValueKind = (row) => {
  const valueKind = String(row?.value_kind || '').toLowerCase();
  return FINANCIAL_VALUE_KINDS.has(valueKind) ? valueKind : null;
};
const nativeFinancialValue = (row, value) => {
  const numeric = finiteValue(value);
  if (!Number.isFinite(numeric)) return Number.NaN;
  const unit = String(row?.original_unit || '').toLowerCase().trim();
  return numeric * (FINANCIAL_UNIT_SCALE[unit] || 1);
};
const financialPeriodYear = (period) => {
  const years = String(period || '').match(/(?:19|20)\d{2}/g)?.map(Number) || [];
  return years.length ? Math.max(...years) : 0;
};
const isFullYearPeriod = (period) => /\bFY\s*\d{2,4}(?:E|A)?\b|full[ -]?year|year (?:ended|ending)/i.test(String(period || ''));
const observationTimestamp = (row) => {
  const value = row.source_reported_at || row.reported_date || row.period_end || row.period_end_or_null || row.updated_at || row.created_at;
  const timestamp = value ? new Date(value).getTime() : 0;
  return Number.isFinite(timestamp) ? timestamp : 0;
};
const periodEndTimestamp = (row) => {
  const value = row.period_end || row.period_end_or_null;
  const timestamp = value ? new Date(value).getTime() : 0;
  return Number.isFinite(timestamp) ? timestamp : 0;
};
const financialCoverageDays = (row) => {
  const explicit = finiteValue(row.coverage_days);
  if (Number.isFinite(explicit)) return explicit;
  const start = row.period_start || row.period_start_or_null;
  const end = row.period_end || row.period_end_or_null;
  if (!start || !end) return 0;
  const derived = Math.floor((new Date(end).getTime() - new Date(start).getTime()) / 86400000) + 1;
  return Number.isFinite(derived) && derived > 0 ? derived : 0;
};
const regionMatches = (row, region) => {
  if (!region) return true;
  if (region === 'Global') return row.region === 'Global' || !row.region;
  return row.region === region;
};
const isTradingRevenueMetric = (row) => {
  if (!row || !TRADING_REVENUE_METRICS.has(row.metric_type)) return false;
  if (row.metric_type !== 'revenue') return true;
  if (['total_revenue', 'fund_revenue', 'proxy_or_component'].includes(row.definition_variant)) return false;
  const definitionText = `${row.definition_variant || ''} ${row.original_value_text || row.original_value || ''} ${row.method_summary || row.notes || ''}`.toLowerCase();
  return ['nti_net_of_direct_fees', 'gross_trading_revenue'].includes(row.definition_variant) || /\b(?:net )?trading revenue\b/.test(definitionText);
};
export const isFinancialObservationDisplayEligible = (row) => {
  if (!row || !isTradingRevenueMetric(row)) return false;
  if (row.record_type !== 'observation') return false;
  if (row.canonical !== true || row.publication_eligible !== true) return false;
  if (['rejected', 'quarantined', 'rework', 'failed', 'withdrawn'].includes(String(row.status || '').toLowerCase())) return false;
  if (!['approved', 'published'].includes(String(row.validation_status || '').toLowerCase())) return false;
  const valueKind = financialValueKind(row);
  if (!RAW_FINANCIAL_VALUE_KINDS.has(valueKind)) return false;
  if (!(row.original_currency || row.currency)) return false;
  if (valueKind === 'range') {
    const low = nativeFinancialValue(row, row.range_low);
    const high = nativeFinancialValue(row, row.range_high);
    return Number.isFinite(low) && Number.isFinite(high) && low <= high;
  }
  return Number.isFinite(nativeFinancialValue(row, row.value));
};

export const selectLatestTradingRevenueObservation = (rows, { region = null } = {}) => (
  (rows || [])
    .filter(row => isFinancialObservationDisplayEligible(row) && regionMatches(row, region))
    .sort((a, b) =>
      periodEndTimestamp(b) - periodEndTimestamp(a)
      || financialCoverageDays(b) - financialCoverageDays(a)
      || financialPeriodYear(b.period) - financialPeriodYear(a.period)
      || observationTimestamp(b) - observationTimestamp(a)
      || (b.metric_type === 'net_trading_income') - (a.metric_type === 'net_trading_income')
      || (FINANCIAL_CONFIDENCE_RANK[b.confidence] || 0) - (FINANCIAL_CONFIDENCE_RANK[a.confidence] || 0)
    )[0] || null
);

export const formatFinancialObservationValue = (row) => {
  if (!row) return '—';
  const valueKind = financialValueKind(row);
  if (!valueKind) return '—';
  const currency = row.original_currency || row.currency;
  const low = nativeFinancialValue(row, row.range_low);
  const high = nativeFinancialValue(row, row.range_high);
  if (valueKind === 'range' && Number.isFinite(low) && Number.isFinite(high)) {
    return `${formatMoney(low, currency)}–${formatMoney(high, currency)}`;
  }
  const value = nativeFinancialValue(row, row.value);
  if (!Number.isFinite(value)) return '—';
  const qualifier = valueKind === 'lower_bound' ? '≥' : valueKind === 'upper_bound' ? '≤' : '';
  return `${qualifier}${formatMoney(value, currency)}`;
};

const financialValueKindLabel = (row) => ({
  point: 'Point value',
  range: 'Range',
  lower_bound: 'Lower bound',
  upper_bound: 'Upper bound',
}[financialValueKind(row)] || 'Value kind unavailable');

const financialMetricLabel = (row) => row?.metric_type === 'net_trading_income'
  ? 'Net Trading Income'
  : row?.metric_type === 'revenue'
    ? 'Trading revenue'
    : 'Net trading revenue';
const financialScopeLabel = (row) => row?.entity_scope || row?.legal_entity_or_null || row?.legal_entity || row?.region || 'Scope not disclosed';
export const financialObservationMetadata = (row) => {
  if (!row) return null;
  const periodStart = row.period_start || row.period_start_or_null;
  const periodEnd = row.period_end || row.period_end_or_null;
  const sourceReportedAt = row.source_reported_at || row.reported_date;
  const normalizationStatus = String(row.normalization_status || '').toLowerCase();
  const normalizationMethod = row.normalization_method || row.transformation_method || row.method_summary;
  const normalizationVersion = row.normalization_version || row.formula_version;
  const normalizedUsd = finiteValue(row.normalized_usd);
  const approvedNormalization = normalizationStatus === 'approved' && Number.isFinite(normalizedUsd) && normalizationMethod && normalizationVersion;
  return {
    period: row.period || 'Period not disclosed',
    periodBounds: periodStart || periodEnd ? `${periodStart ? fmtDateFull(periodStart) : '—'} to ${periodEnd ? fmtDateFull(periodEnd) : '—'}` : 'Period bounds not disclosed',
    metricAsOf: row.as_of_date || periodEnd ? fmtDateFull(row.as_of_date || periodEnd) : 'Metric as-of not disclosed',
    reported: sourceReportedAt ? fmtDateFull(sourceReportedAt) : 'Source report date not disclosed',
    scope: financialScopeLabel(row),
    definition: row.definition_variant || 'Definition variant not disclosed',
    accountingBasis: row.accounting_basis || row.accounting_basis_or_null || 'Accounting basis not disclosed',
    precision: row.precision || 'Precision not stated',
    valueKind: financialValueKindLabel(row),
    transformation: approvedNormalization
      ? `Approved normalized USD: ${formatMoney(normalizedUsd, 'USD')} · ${normalizationMethod} (${normalizationVersion})`
      : Number.isFinite(normalizedUsd)
        ? 'Normalized value withheld: approval and method lineage not both exposed'
        : 'Native value; no approved normalization shown',
    comparability: row.comparability_class ? `Comparability ${row.comparability_class} · ${row.rank_eligible === true ? 'Rank eligible' : 'Unranked'}` : 'Comparability not assessed (unranked)',
    method: row.method_summary || row.notes || null,
    evidenceLocator: row.evidence_locator || row.evidence_locator_or_excerpt || null,
  };
};
const DAY_MS = 86400000;
export const isWithinDays = (value, days, now = new Date()) => {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return date >= new Date(now.getTime() - days * DAY_MS) && date <= now;
};
const isApacLocation = (loc) => APAC_LOCATIONS.some(a => loc?.includes(a));
const sentColor = (s) => s === 'positive' ? '#008753' : s === 'negative' ? '#ff3300' : '#011735';
const confBadge = (c) => FINANCIAL_CONFIDENCE_META[c] || { bg:'rgba(94,126,160,0.1)', color:'#3c5b7c', icon:'?', label:c || 'Unknown' };
const DISPLAY_NAMES = {'Hudson River Trading':'HRT','Qube Research & Technologies':'QRT','Susquehanna International Group':'SIG'};
const displayName = (name) => DISPLAY_NAMES[name] || name;

function ExpandableText({ text, limit = 150, style: s = {} }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const isLong = text.length > limit;
  return (
    <div style={{...s, cursor: isLong ? 'pointer' : 'default'}} onClick={() => isLong && setExpanded(e => !e)}>
      {expanded || !isLong ? text : `${text.slice(0, limit)}...`}
      {isLong && (
        <span style={{marginLeft:4,fontSize:10,fontWeight:600,color:'var(--accent)'}}>{expanded ? '↑ less' : '↓ more'}</span>
      )}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=Inter:wght@400;500;600;700&family=Newsreader:opsz,wght@6..72,400;6..72,500&display=swap');
*{box-sizing:border-box;margin:0;padding:0}
:root{--bg:#f4f7fa;--surface:#ffffff;--surface-soft:#ebf5fd;--border:#cfe3f4;--text:#011735;--muted:#5e7ea0;--accent:#011735;--accent2:#ff3300;--green:#008753;--purple:#7f00ff;--mono:'IBM Plex Mono',monospace;--sans:'Inter',sans-serif;--serif:'Newsreader',Georgia,serif}
body{background:var(--bg);color:var(--text);font-family:var(--sans)}
::-webkit-scrollbar{width:5px;height:5px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:#94adbf;border-radius:3px}
@keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
@keyframes slideIn{from{opacity:0;transform:translateX(-8px)}to{opacity:1;transform:translateX(0)}}
@keyframes ticker{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
@keyframes pulse{0%,100%{opacity:.5}50%{opacity:1}}
.fade-up{animation:fadeUp .4s ease forwards}
.gptn-shell h1,.gptn-shell h2{font-family:var(--serif);font-weight:500;letter-spacing:-.02em}
.card{background:var(--surface);border:1px solid var(--border);border-radius:8px;padding:16px;box-shadow:none;transition:border-color .18s ease,transform .18s ease}
.card:hover{border-color:rgba(1,23,53,.28)}
.section-title{font-family:var(--mono);font-size:11px;font-weight:600;color:var(--muted);letter-spacing:.09em;text-transform:uppercase;margin-bottom:10px}
.mono{font-family:var(--mono)}
.btn{padding:6px 14px;min-height:32px;border-radius:6px;border:1px solid var(--border);background:transparent;color:var(--muted);font-size:12px;font-weight:600;cursor:pointer;font-family:var(--sans);transition:all .15s ease}
.btn:hover{border-color:var(--accent);color:var(--text);background:rgba(1,23,53,.04)}
.btn-active{background:var(--accent);color:#fff;border-color:var(--accent)}
.btn-active:hover{background:#11274a;color:#fff}
.news-ticker-track:hover{animation-play-state:paused}
.nti-bar{position:relative}.nti-bar:hover .nti-tooltip{display:block}
.nti-tooltip{display:none;position:absolute;top:-32px;left:50%;transform:translateX(-50%);background:#011735;color:#fff;padding:4px 8px;border-radius:4px;font-size:12px;white-space:nowrap;z-index:10;border:1px solid #11274a;pointer-events:none}
.clickable-row{transition:all .12s}.clickable-row:hover{background:rgba(1,23,53,.04) !important}
.filter-btn{padding:3px 8px;border-radius:4px;border:1px solid var(--border);background:transparent;color:var(--muted);font-size:13px;font-weight:600;cursor:pointer;font-family:var(--sans);transition:all .12s}
.filter-btn:hover{border-color:var(--accent);color:var(--text)}
.filter-btn-active{background:rgba(255,51,0,.08);color:var(--accent2);border-color:rgba(255,51,0,.25)}
.gptn-header{position:sticky;top:0;z-index:40;background:#021129;color:#fff;border-bottom:1px solid rgba(255,255,255,.14)!important;box-shadow:0 8px 28px rgba(1,23,53,.12)}
.gptn-header .gptn-brand-name{color:#fff}
.gptn-header .gptn-brand-subtitle{color:rgba(255,255,255,.58)!important}
.gptn-brand-mark{display:inline-flex;align-items:flex-end;justify-content:center;gap:2px;width:28px;height:28px;padding:5px;border:1px solid rgba(255,255,255,.14);border-radius:6px;background:#11274a}
.gptn-brand-mark i{display:block;width:4px;background:var(--accent2);transform:skew(-16deg)}
.gptn-brand-mark i:nth-child(1){height:10px}.gptn-brand-mark i:nth-child(2){height:16px}.gptn-brand-mark i:nth-child(3){height:7px}
.gptn-live{display:inline-flex;align-items:center;gap:5px;color:#39d377!important;background:rgba(57,211,119,.1)!important;border:1px solid rgba(57,211,119,.18)}
.gptn-live:before{content:'';width:5px;height:5px;border-radius:50%;background:#39d377}
.gptn-nav{align-self:stretch}
.gptn-nav .btn{align-self:stretch;min-height:52px;border:0;border-bottom:2px solid transparent;border-radius:0;color:rgba(255,255,255,.6);background:transparent}
.gptn-nav .btn:hover{color:#fff;background:rgba(255,255,255,.05);border-bottom-color:rgba(255,255,255,.2)}
.gptn-nav .btn-active,.gptn-nav .btn-active:hover{color:#fff;background:transparent;border-bottom-color:var(--accent2)}
.gptn-nav .gptn-refresh{min-width:36px;color:rgba(255,255,255,.7);border:1px solid rgba(255,255,255,.16);border-radius:6px;align-self:center;min-height:32px}
.gptn-main{width:100%}
.gptn-footer{background:#021129;color:rgba(255,255,255,.55);border-top:1px solid #11274a!important}
.gptn-footer div{color:inherit!important}
.gptn-password-gate{color:#fff;background:radial-gradient(circle at 75% 20%,rgba(127,0,255,.18),transparent 28%),#021129!important}
.gptn-password-gate .gptn-access-title{font-family:var(--serif);font-size:48px;font-weight:400;line-height:1;color:#fff}
.gptn-password-gate .gptn-access-title em{color:var(--accent2);font-weight:400}
.gptn-password-gate .gptn-access-subtitle{color:rgba(255,255,255,.58)!important}
.gptn-password-gate input{background:#11274a!important;color:#fff!important;border-color:rgba(255,255,255,.18)!important}
.gptn-password-gate input::placeholder{color:rgba(255,255,255,.45)}
.gptn-password-gate .btn-active{background:var(--accent2);border-color:var(--accent2)}
.gptn-access-disclaimer{--text:#011735;--muted:#5e7ea0;--accent:#011735;padding:8px;border:1px solid rgba(255,255,255,.14);border-radius:10px;background:#fff}
@media(max-width:900px){
  .gptn-header{height:auto!important;min-height:56px;flex-wrap:wrap;padding:0 14px!important}
  .gptn-nav{order:3;width:100%;overflow-x:auto;gap:0!important}
  .gptn-nav .btn{flex:0 0 auto;min-height:42px;white-space:nowrap}
  .gptn-main{padding:14px!important}
  .gptn-competitor-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important}
  .gptn-primary-grid,.gptn-people-grid{grid-template-columns:1fr!important}
  .gptn-hiring-layout{flex-direction:column}
  .gptn-hiring-layout>div{width:100%}
}
@media(max-width:560px){
  .gptn-brand-subtitle{display:none}
  .gptn-competitor-grid{grid-template-columns:1fr!important}
  .gptn-password-gate .gptn-access-title{font-size:40px}
  .gptn-dossier{flex-direction:column;min-height:0!important}
  .gptn-dossier-sidebar{width:100%!important;border-right:0!important;border-bottom:1px solid var(--border);padding-bottom:8px}
  .gptn-dossier-tabs{display:flex;overflow-x:auto;padding:0 8px}
  .gptn-dossier-tab{width:auto!important;flex:0 0 auto;border-left:0!important;border-bottom:2px solid transparent!important;white-space:nowrap}
  .gptn-dossier-tab-active{border-bottom-color:var(--accent)!important}
  .gptn-dossier-summary{display:none}
  .gptn-dossier-content{max-height:none!important;overflow:visible!important;padding:16px 0!important}
}
@media(prefers-reduced-motion:reduce){*,*::before,*::after{animation-duration:.01ms!important;transition-duration:.01ms!important}}
`;

// ═════════════════════════════════════════════════════════════
// COMPONENTS
// ═════════════════════════════════════════════════════════════

function NewsTicker({ items }) {
  if (!items?.length) return null;
  const doubled = [...items, ...items];
  return (
    <div style={{overflow:'hidden',background:'rgba(1,23,53,0.04)',borderBottom:'1px solid rgba(1,23,53,0.12)',padding:'6px 0'}}>
      <div className="news-ticker-track" style={{display:'flex',gap:40,whiteSpace:'nowrap',animation:`ticker ${items.length * 5}s linear infinite`}}>
        {doubled.map((n, i) => (
          <span key={i} style={{fontSize:13,display:'flex',alignItems:'center',gap:6,cursor:n.url?'pointer':'default'}} onClick={()=>n.url&&window.open(n.url,'_blank')}>
            <span style={{width:5,height:5,borderRadius:99,background:sentColor(n.sentiment),flexShrink:0}}/>
            <span style={{color:'var(--text)',fontWeight:500}}>{n.headline}</span>
            <span style={{color:'var(--muted)',fontSize:12}}>{n.source} · {fmtDate(n.pub_date)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}



function DisclaimerBanner() {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{background:'rgba(1,23,53,0.04)',border:'1px solid rgba(1,23,53,0.15)',borderRadius:expanded?10:6,padding:expanded?'12px 16px':'6px 14px',marginBottom:10,cursor:'pointer',transition:'all .2s'}} onClick={()=>setExpanded(e=>!e)}>
      {expanded ? (
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
          <div style={{display:'flex',gap:8,alignItems:'flex-start'}}>
            <span style={{fontSize:16,flexShrink:0,marginTop:1}}>⚠️</span>
            <div style={{fontSize:12,color:'#5e7ea0',lineHeight:1.6}}>
              <div style={{fontSize:13,fontWeight:700,color:'var(--accent)',marginBottom:4,letterSpacing:'.04em',textTransform:'uppercase'}}>Important Notices</div>
              <div style={{marginBottom:3}}><span style={{fontWeight:600,color:'var(--text)'}}>1. Data Sources:</span> This dashboard is built on publicly available information collected from news outlets, company websites, job boards, LinkedIn, and social media platforms. No proprietary or confidential data is used.</div>
              <div style={{marginBottom:3}}><span style={{fontWeight:600,color:'var(--text)'}}>2. Refresh Cadence:</span> Dashboard data follows a weekly collection and validation process. Source publication, retrieval, event, and metric dates may differ.</div>
              <div style={{marginBottom:3}}><span style={{fontWeight:600,color:'var(--text)'}}>3. Accuracy Disclaimer:</span> While every effort is made to ensure accuracy, GPTN makes no guarantees regarding the completeness or correctness of any data presented. Financial figures marked "estimated" or "rumoured" should be treated with appropriate caution.</div>
              <div style={{marginBottom:3}}><span style={{fontWeight:600,color:'var(--text)'}}>4. Non-Commercial Use:</span> This dashboard is for informational and personal research purposes only. It is not intended for commercial use, redistribution, or resale.</div>
              <div style={{marginBottom:3}}><span style={{fontWeight:600,color:'var(--text)'}}>5. No Investment Advice:</span> Nothing on this dashboard constitutes financial, investment, legal, or professional advice. Users should conduct their own due diligence before making any decisions based on the information presented.</div>
              <div><span style={{fontWeight:600,color:'var(--text)'}}>6. Copyright:</span> All third-party trademarks, logos, and company names referenced herein are the property of their respective owners. GPTN is an independent project and is not affiliated with, endorsed by, or sponsored by any of the firms tracked.</div>
            </div>
          </div>
          <span style={{fontSize:12,color:'var(--muted)',flexShrink:0,marginLeft:8,fontWeight:600}}>▲ collapse</span>
        </div>
      ) : (
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:13}}>⚠️</span>
          <span style={{fontSize:12,color:'var(--muted)',flex:1}}>
            <span style={{fontWeight:600,color:'var(--accent)'}}>Disclaimer:</span> Public data only · Weekly refresh · Not investment advice · Non-commercial use · <span style={{color:'#3c5b7c',fontWeight:500}}>Click to read full notices</span>
          </span>
          <span style={{fontSize:12,color:'var(--muted)',fontWeight:600}}>▼</span>
        </div>
      )}
    </div>
  );
}

export const getRecentNews = (news, limit = 10, now = new Date()) => (news||[])
  .filter(n => {
    const publishedAt = new Date(n.pub_date);
    return isRelevantNews(n)
      && typeof n.url === 'string'
      && n.url.trim().length > 0
      && !Number.isNaN(publishedAt.getTime())
      && publishedAt <= now;
  })
  .sort((a,b) => new Date(b.pub_date) - new Date(a.pub_date))
  .slice(0, limit);

export const isCoverageOutcomeDisplayEligible = (row) => (
  row?.record_type === 'coverage_outcome'
  && row.outcome_code === 'no_new_qualifying_disclosure'
  && row.coverage_scope === 'monitored_public_sources'
  && row.canonical === true
  && row.publication_eligible === true
  && ['approved', 'published'].includes(String(row.validation_status || '').toLowerCase())
  && !['rejected', 'quarantined', 'rework', 'failed', 'withdrawn'].includes(String(row.status || '').toLowerCase())
  && Boolean(row.competitor_id && row.window_start && row.window_end && row.checked_at && row.source_set_version)
  && Number.isFinite(finiteValue(row.checked_source_count))
);

const selectLatestCoverageOutcome = (rows) => (
  (rows || [])
    .filter(isCoverageOutcomeDisplayEligible)
    .sort((a, b) =>
      new Date(b.window_end).getTime() - new Date(a.window_end).getTime()
      || new Date(b.checked_at).getTime() - new Date(a.checked_at).getTime()
    )[0] || null
);

export function FinancialObservationTable({ competitors = [], financials = [], coverageResults = [], onSelectCompetitor = () => {}, targetYear = new Date().getFullYear() }) {
  const rows = competitors.map(comp => {
    const observation = selectLatestTradingRevenueObservation(financials.filter(row => row.competitor_id === comp.id), { region: 'Global' });
    const coverageOutcome = observation ? null : selectLatestCoverageOutcome(coverageResults.filter(row => row.competitor_id === comp.id));
    const observationYear = financialPeriodYear(observation?.period);
    const periodStatus = !observation
      ? coverageOutcome
        ? `No qualifying new disclosure found in monitored sources through ${fmtDateFull(coverageOutcome.window_end)}`
        : `No approved ${targetYear} NTI / trading-revenue observation available`
      : observationYear < targetYear
        ? `No ${targetYear} observation; latest prior period shown`
        : !isFullYearPeriod(observation.period)
          ? `No full-year ${targetYear} estimate; partial period shown`
          : `Full-year ${targetYear} ${financialValueKindLabel(observation).toLowerCase()}`;
    return { comp, observation, coverageOutcome, meta: financialObservationMetadata(observation), periodStatus };
  });
  const availableCount = rows.filter(row => row.observation).length;

  return (
    <>
      <div id="financial-observation-summary" style={{fontSize:11,color:'var(--opt-info)',lineHeight:1.45,marginBottom:6}}>
        {availableCount} of {competitors.length} firms have a latest NTI / trading-revenue observation. Values use native currency and firm order; missing or non-comparable data are not ranked.
      </div>
      <div style={{overflowX:'auto'}}>
        <table aria-describedby="financial-observation-summary" style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
          <caption style={{position:'absolute',width:1,height:1,padding:0,margin:-1,overflow:'hidden',clip:'rect(0,0,0,0)',whiteSpace:'nowrap',border:0}}>Latest competitor NTI and trading-revenue observations with evidence and methodology</caption>
          <thead>
            <tr>
              <th scope="col" style={{textAlign:'left',padding:'4px',borderBottom:'1px solid var(--border)'}}>Firm</th>
              <th scope="col" style={{textAlign:'right',padding:'4px',borderBottom:'1px solid var(--border)'}}>Observation</th>
              <th scope="col" style={{textAlign:'left',padding:'4px',borderBottom:'1px solid var(--border)'}}>Period, scope and evidence</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ comp, observation, coverageOutcome, meta, periodStatus }) => {
              const confidence = observation ? confBadge(observation.confidence) : confBadge(coverageOutcome?.confidence || 'insufficient_evidence');
              return (
                <tr key={comp.id}>
                  <th scope="row" style={{textAlign:'left',verticalAlign:'top',padding:'7px 4px',borderBottom:'1px solid var(--border)'}}>
                    <button type="button" onClick={()=>onSelectCompetitor(comp)} style={{fontSize:12,fontWeight:600,color:'var(--text)',border:0,padding:0,background:'transparent',cursor:'pointer',textAlign:'left'}}>{displayName(comp.name)}</button>
                  </th>
                  <td className="mono" style={{textAlign:'right',verticalAlign:'top',padding:'7px 4px',borderBottom:'1px solid var(--border)',fontWeight:700,color:'var(--text)',whiteSpace:'nowrap'}}>
                    <div>{formatFinancialObservationValue(observation)}</div>
                    <span style={{display:'inline-block',fontFamily:'var(--sans)',fontSize:9,padding:'1px 4px',borderRadius:99,background:confidence.bg,color:confidence.color,fontWeight:700}}>{confidence.icon} {confidence.label}</span>
                  </td>
                  <td style={{verticalAlign:'top',padding:'7px 4px',borderBottom:'1px solid var(--border)',color:'var(--opt-info)',lineHeight:1.45}}>
                    <div style={{fontWeight:600,color:observation?'var(--text)':'var(--opt-info)'}}>{periodStatus}</div>
                    {observation ? <>
                      <div>{financialMetricLabel(observation)} · {meta.period} · {meta.valueKind}</div>
                      <div>Period bounds: {meta.periodBounds} · Metric as-of: {meta.metricAsOf} · Source reported: {meta.reported}</div>
                      <div>Scope: {meta.scope} · Definition: {meta.definition} · Precision: {meta.precision}</div>
                      <div>Accounting basis: {meta.accountingBasis}</div>
                      <div>{meta.transformation} · {meta.comparability}</div>
                      {observation.source_url ? <a href={observation.source_url} target="_blank" rel="noreferrer" style={{color:'#3c5b7c'}}>Evidence: {observation.source || 'source'} ↗</a> : <div>Evidence link unavailable{observation.source ? ` · ${observation.source}` : ''}</div>}
                      {(meta.method || meta.evidenceLocator) && <details><summary style={{cursor:'pointer',color:'#3c5b7c'}}>Method / evidence locator</summary><div style={{marginTop:3,maxWidth:520}}>{meta.method && <div>Method: {meta.method}</div>}{meta.evidenceLocator && <div>Evidence locator: {meta.evidenceLocator}</div>}</div></details>}
                    </> : coverageOutcome ? <>
                      <div>Coverage window: {fmtDateFull(coverageOutcome.window_start)} to {fmtDateFull(coverageOutcome.window_end)}</div>
                      <div>Checked at: {fmtDateFull(coverageOutcome.checked_at)} · Coverage scope: monitored public sources · Source set: {coverageOutcome.source_set_version} · Sources checked: {coverageOutcome.checked_source_count}</div>
                      {coverageOutcome.source_url ? <a href={coverageOutcome.source_url} target="_blank" rel="noreferrer" style={{color:'#3c5b7c'}}>Coverage evidence ↗</a> : <div>Coverage evidence link unavailable</div>}
                      {(coverageOutcome.summary || coverageOutcome.evidence_locator) && <details><summary style={{cursor:'pointer',color:'#3c5b7c'}}>Coverage method / evidence locator</summary><div style={{marginTop:3,maxWidth:520}}>{coverageOutcome.summary && <div>{coverageOutcome.summary}</div>}{coverageOutcome.evidence_locator && <div>Evidence locator: {coverageOutcome.evidence_locator}</div>}</div></details>}
                    </> : <div className="mono">— <span style={{fontFamily:'var(--sans)'}}>No approved observation or published coverage outcome</span></div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{marginTop:5,fontSize:10,color:'var(--opt-info)'}}>Comparison requires aligned period, currency, entity scope, definition and approved comparability metadata. Class A only is rank-eligible by default.</div>
    </>
  );
}

export function RecentNews({ data }) {
  const { news, competitors } = data;
  const recentNews = getRecentNews(news);
  const compName = (id) => { const c = (competitors||[]).find(c=>c.id===id); return c ? displayName(c.name) : '—'; };
  const isNew = (d) => isWithinDays(d, 7);

  if (recentNews.length === 0) return null;

  return (
    <div className="card fade-up" style={{borderColor:'rgba(1,23,53,0.15)',background:'linear-gradient(135deg,rgba(1,23,53,0.03),rgba(1,23,53,0.02))',height:'100%',display:'flex',flexDirection:'column'}}>
      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:8}}>
        <span style={{fontSize:14}}>🔥</span>
        <h3 style={{fontSize:12,fontWeight:600,color:'var(--accent)',letterSpacing:'.06em',textTransform:'uppercase'}}>Top Recent News</h3>
      </div>

      <div style={{display:'flex',flexDirection:'column',gap:3,flex:1}}>
        {recentNews.map((n,i) => (
          <a key={n.id || n.url} href={n.url} target="_blank" rel="noopener noreferrer" style={{display:'flex',gap:6,alignItems:'flex-start',padding:'3px 6px',background:i<3?'rgba(1,23,53,0.03)':'transparent',borderRadius:4,textDecoration:'none',color:'inherit'}}>
            <div style={{display:'flex',alignItems:'center',gap:3,flexShrink:0,minWidth:14}}>
              <span className="mono" style={{fontSize:10,fontWeight:700,color:i<3?'var(--accent)':'var(--muted)'}}>{i+1}</span>
              <div style={{width:4,height:4,borderRadius:99,background:sentColor(n.sentiment),flexShrink:0}}/>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,lineHeight:1.3,fontWeight:i<3?600:400}}>
                {isNew(n.pub_date) && <span style={{fontSize:10,marginRight:3}}>🆕</span>}
                {n.headline}
                {n.url && <span style={{fontSize:10,color:'#3c5b7c',marginLeft:4}}>↗</span>}
              </div>
              <div style={{fontSize:10,color:'var(--muted)',marginTop:1}}>
                <span style={{padding:'0px 3px',borderRadius:2,background:'rgba(1,23,53,0.08)',color:'var(--accent)',fontSize:9,fontWeight:600,marginRight:3}}>{compName(n.competitor_id)}</span>
                {n.source} · {fmtDate(n.pub_date)}
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

function QuadrantCard({title, icon, children, accentColor, style:s}) {
  return (
    <div className="card fade-up" style={{...s}}>
      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:12}}>
        <span style={{fontSize:16}}>{icon}</span>
        <h3 style={{fontSize:13,fontWeight:600,color:accentColor||'var(--accent)',letterSpacing:'.06em',textTransform:'uppercase'}}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

function CompetitorPill({comp, isActive, onClick}) {
  return (
    <button onClick={onClick} style={{
      padding:'8px 12px',borderRadius:8,border:'1px solid',cursor:'pointer',fontFamily:'var(--sans)',textAlign:'left',transition:'all .15s',
      borderColor:isActive?'var(--accent)':'var(--border)',background:isActive?'rgba(1,23,53,0.05)':'var(--surface)',display:'flex',alignItems:'center',gap:8,width:'100%',
    }}>
      <span className="mono" style={{
        width:30,height:30,borderRadius:6,fontSize:11,fontWeight:700,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',
        background:isActive?'linear-gradient(135deg,#011735,#ff3300)':'#ebf5fd',color:isActive?'#fff':'var(--muted)',
      }}>{comp.logo_url || comp.name?.split(' ').map(w=>w[0]).join('').slice(0,2)}</span>
      <div style={{minWidth:0,flex:1}}>
        <div style={{fontSize:13,fontWeight:600,color:isActive?'var(--text)':'#5e7ea0',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{displayName(comp.name)}</div>
        <div style={{fontSize:12,color:'var(--muted)'}}>{comp.hq}</div>
      </div>
    </button>
  );
}

// ═════════════════════════════════════════════════════════════
// PAGES
// ═════════════════════════════════════════════════════════════

function HomePage({ data, onSelectCompetitor }) {
  const { competitors, peopleMoves, news, jobCounts, jobListings } = data;
  const [movesFilter, setMovesFilter] = useState('all');
  
  // Junk filter imported from ./config

  const recentNews = (news||[]).filter(n => {
    if (!n.pub_date) return false;
    const d = new Date(n.pub_date); const now = new Date();
    return (now - d) / 86400000 < 21 && isRelevantNews(n);
  }).slice(0, 30);

  // Deduplicate similar headlines — keep first (most recent) per story
  const dedupeNews = (items) => {
    const seen = [];
    return items.filter(n => {
      const words = (n.headline||'').toLowerCase().replace(/[^a-z0-9 ]/g,'').split(' ').filter(w=>w.length>3);
      const isDupe = seen.some(prev => {
        const overlap = words.filter(w => prev.includes(w)).length;
        return overlap / Math.max(words.length, 1) > 0.6;
      });
      if (!isDupe) { seen.push(words); return true; }
      return false;
    });
  };
  const tickerNews = dedupeNews(recentNews);

  const recentMoves = (peopleMoves||[]).sort((a,b) => new Date(b.move_date||b.announced_date||0) - new Date(a.move_date||a.announced_date||0)).slice(0, 20);
  
  // Aggregate APAC job counts
  const topApacHirer = Object.entries(jobCounts||{}).sort(([,a],[,b])=>b.apac-a.apac)[0];

  // Build hiring heatmap: firm × region × department
  const hiringByFirmRegion = {};
  const hiringByFirmDept = {};
  const allRegions = new Set();
  const allDepts = new Set();
  (jobListings||[]).forEach(j => {
    const comp = (competitors||[]).find(c => c.id === j.competitor_id);
    if (!comp || !isApacLocation(j.location)) return;
    const firm = comp.name;
    const loc = groupLoc(j.location);
    const dept = (j.department === 'Business Operations' || j.department === 'Operations') ? 'Operations' : (j.department || 'Other');
    allRegions.add(loc);
    allDepts.add(dept);
    if (!hiringByFirmRegion[firm]) hiringByFirmRegion[firm] = {};
    hiringByFirmRegion[firm][loc] = (hiringByFirmRegion[firm][loc]||0) + 1;
    if (!hiringByFirmDept[firm]) hiringByFirmDept[firm] = {};
    hiringByFirmDept[firm][dept] = (hiringByFirmDept[firm][dept]||0) + 1;
  });
  const sortedRegions = [...allRegions].sort();
  // Show top departments + an "Other" bucket so row totals match
  const allDeptsSorted = [...allDepts].map(d => [d, Object.values(hiringByFirmDept).reduce((s,fd)=>s+(fd[d]||0),0)]).sort(([,a],[,b])=>b-a);
  const topDepts = allDeptsSorted.slice(0,6).map(([d])=>d);
  const otherDepts = allDeptsSorted.slice(6).map(([d])=>d);
  const heatmapFirms = (competitors||[]).filter(c => (jobCounts?.[c.name]?.apac||0) > 0).sort((a,b) => (jobCounts?.[b.name]?.apac||0) - (jobCounts?.[a.name]?.apac||0));

  // Compute firm APAC totals from the heatmap data for verification
  const firmApacTotal = (firm) => Object.values(hiringByFirmRegion[firm]||{}).reduce((s,v)=>s+v,0);

  // Global max across all cells for consistent shading
  const globalMaxRegion = Math.max(...Object.values(hiringByFirmRegion).flatMap(f=>Object.values(f)), 1);
  const globalMaxDept = Math.max(...Object.values(hiringByFirmDept).flatMap(f=>Object.values(f)), 1);

  // Top hirer department focus summary
  const topHirerDepts = hiringByFirmDept[topApacHirer?.[0]] || {};
  const topHirerDeptsSorted = Object.entries(topHirerDepts).sort(([,a],[,b])=>b-a).slice(0,3);
  const topHirerFocusSummary = topHirerDeptsSorted.length > 0 
    ? `Focused on ${topHirerDeptsSorted.map(([d])=>d).join(', ')}` 
    : '';

  // New markers follow the seven-day weekly collection window.
  const isNewItem = (d) => isWithinDays(d, 7);

  return (
    <div className="fade-up">
      {/* Disclaimer / Caveats */}
      {/* News Ticker */}
      <NewsTicker items={tickerNews} />

      {/* Competitor Grid — moved to top */}
      <div style={{padding:'12px 0 8px'}}>
        <div className="section-title" style={{padding:'0 4px'}}>COMPETITOR OVERVIEW — Click for details</div>
        <div className="gptn-competitor-grid" style={{display:'grid',gridTemplateColumns:'repeat(5, 1fr)',gap:8}}>
          {(competitors||[]).map((c,i) => (
            <CompetitorPill key={c.id} comp={c} isActive={false} onClick={()=>onSelectCompetitor(c)} />
          ))}
        </div>
      </div>

      {/* Top News (left) + Performance Intelligence (right) — side by side */}
      <div className="gptn-primary-grid" style={{display:'grid',gridTemplateColumns:'minmax(280px,.7fr) minmax(0,1.6fr)',gap:12,padding:'14px 0 12px'}}>
        {/* Recent news — compact */}
        <RecentNews data={data} />

        {/* Performance Intelligence */}
        <QuadrantCard title="Performance Intelligence" icon="📊" accentColor="#008753" style={{minWidth:0}}>
          <PerformanceIntelligenceChart
            competitors={competitors || []}
            observations={data.performanceObservations || []}
            scenarios={data.financialScenarios || []}
            onSelectCompetitor={onSelectCompetitor}
          />
        </QuadrantCard>
      </div>

      {/* People Moves — full width with firm filter */}
      <div style={{marginBottom:16}}>
        <QuadrantCard title="People Moves" icon="🔄" accentColor="var(--accent2)">
          {(() => {
            // Get unique firms from moves — only include tracked competitor names
            const competitorNames = new Set((competitors||[]).map(c => c.name));
            const moveFirms = [...new Set(recentMoves.flatMap(m => [m.new_firm, m.previous_firm].filter(Boolean)))].filter(f => competitorNames.has(f)).sort();
            const filtered = movesFilter === 'all' ? recentMoves : recentMoves.filter(m => m.new_firm === movesFilter || m.previous_firm === movesFilter);
            return (
              <>
                <div style={{display:'flex',gap:4,marginBottom:10,flexWrap:'wrap'}}>
                  <button className={`filter-btn ${movesFilter==='all'?'filter-btn-active':''}`} onClick={()=>setMovesFilter('all')}>All ({recentMoves.length})</button>
                  {moveFirms.slice(0,8).map(f => (
                    <button key={f} className={`filter-btn ${movesFilter===f?'filter-btn-active':''}`} onClick={()=>setMovesFilter(movesFilter===f?'all':f)}>
                      {displayName(f.length > 14 ? f.split(' ')[0] : f)}
                    </button>
                  ))}
                </div>
                <div className="gptn-people-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
                  {filtered.length === 0 ? <div style={{fontSize:13,color:'var(--muted)',textAlign:'center',padding:16,gridColumn:'1/-1'}}>No people moves {movesFilter !== 'all' ? `for ${movesFilter}` : 'recorded yet'}</div> :
                    filtered.map((m,i) => (
                      <div key={i} className="clickable-row" style={{padding:'8px 10px',background:'var(--bg)',borderRadius:6,animation:`slideIn .3s ease forwards`,animationDelay:`${i*0.05}s`,opacity:0}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                          <div>
                            <div style={{fontSize:13,fontWeight:600}}>
                              {isNewItem(m.move_date || m.announced_date) && <span style={{fontSize:10,marginRight:3}}>🆕</span>}
                              {m.person_name}
                            </div>
                            <div style={{fontSize:12,color:'var(--muted)',lineHeight:1.4}}>
                              {m.previous_firm && <><span style={{color:'var(--accent2)'}}>{displayName(m.previous_firm)}</span> → </>}
                              <span style={{color:'var(--green)'}}>{displayName(m.new_firm) || '—'}</span>
                            </div>
                            <div style={{fontSize:11,color:'var(--muted)'}}>{m.new_role} · {m.location}</div>
                          </div>
                          <div style={{textAlign:'right',flexShrink:0}}>
                            <div style={{fontSize:11,color:'var(--muted)'}}>{fmtDateDMY(m.move_date || m.announced_date)}</div>
                            {m.is_confirmed && <span style={{fontSize:10,padding:'1px 4px',borderRadius:99,background:'rgba(0,135,83,0.1)',color:'#008753',fontWeight:600}}>✓</span>}
                          </div>
                        </div>
                        {m.impact_analysis && <div style={{marginTop:4,borderTop:'1px solid var(--border)',paddingTop:4}}><ExpandableText text={m.impact_analysis} limit={150} style={{fontSize:11,color:'#5e7ea0',lineHeight:1.35}} /></div>}
                      </div>
                    ))
                  }
                </div>
              </>
            );
          })()}
        </QuadrantCard>
      </div>
      
      {/* Overview Panel: Hiring Heatmap + Top Hirer */}
      <div className="gptn-hiring-layout" style={{display:'flex',gap:12,padding:'0 0 16px 0'}}>
        
        {/* Hiring Heatmap — Firm × Region */}
        <div style={{flex:3,background:'var(--surface)',borderRadius:10,padding:14,border:'1px solid var(--border)',overflowX:'auto'}}>
          <div className="section-title">APAC Hiring Heatmap — Firm × City</div>
          {heatmapFirms.length === 0 ? (
            <div style={{fontSize:13,color:'var(--muted)',textAlign:'center',padding:20}}>No APAC job data available</div>
          ) : (
            <div style={{display:'grid',gridTemplateColumns:`100px repeat(${sortedRegions.length}, 1fr) 48px`,gap:2,fontSize:11}}>
              {/* Header row */}
              <div style={{fontWeight:600,color:'var(--muted)',padding:'4px 2px'}}></div>
              {sortedRegions.map(r => (
                <div key={r} style={{fontWeight:600,color:'var(--muted)',textAlign:'center',padding:'4px 2px',whiteSpace:'nowrap'}}>{r}</div>
              ))}
              <div style={{fontWeight:600,color:'var(--accent)',textAlign:'center',padding:'4px 2px'}}>Total</div>
              {/* Data rows */}
              {heatmapFirms.map(c => {
                const firmData = hiringByFirmRegion[c.name] || {};
                const rowTotal = firmApacTotal(c.name);
                return [
                  <div key={`${c.name}-label`} style={{fontWeight:600,color:'var(--text)',padding:'4px 2px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',cursor:'pointer'}} onClick={()=>onSelectCompetitor(c)}>
                    {displayName(c.name)}
                  </div>,
                  ...sortedRegions.map(r => {
                    const val = firmData[r] || 0;
                    const intensity = val > 0 ? Math.max(0.12, val / globalMaxRegion) : 0;
                    return (
                      <div key={`${c.name}-${r}`} style={{
                        textAlign:'center',padding:'4px 2px',borderRadius:3,fontFamily:'var(--mono)',fontWeight:600,
                        background: val > 0 ? `rgba(1,23,53,${intensity})` : 'rgba(0,0,0,0.02)',
                        color: val > 0 ? (intensity > 0.5 ? '#fff' : 'var(--accent)') : 'var(--border)',
                      }}>
                        {val || '·'}
                      </div>
                    );
                  }),
                  <div key={`${c.name}-total`} className="mono" style={{textAlign:'center',padding:'4px 2px',fontWeight:700,color:'var(--accent)',background:'rgba(1,23,53,0.05)',borderRadius:3}}>
                    {rowTotal}
                  </div>
                ];
              })}
            </div>
          )}
          
          {/* Firm × Department mini heatmap */}
          {topDepts.length > 0 && heatmapFirms.length > 0 && (
            <>
              <div className="section-title" style={{marginTop:14}}>APAC Hiring — Firm × Department</div>
              <div style={{display:'grid',gridTemplateColumns:`100px repeat(${topDepts.length}, 1fr) 40px 48px`,gap:2,fontSize:11}}>
                <div style={{fontWeight:600,color:'var(--muted)',padding:'4px 2px'}}></div>
                {topDepts.map(d => (
                  <div key={d} style={{fontWeight:600,color:'var(--muted)',textAlign:'center',padding:'4px 2px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{d}</div>
                ))}
                <div style={{fontWeight:600,color:'var(--muted)',textAlign:'center',padding:'4px 2px'}}>Other</div>
                <div style={{fontWeight:600,color:'#3c5b7c',textAlign:'center',padding:'4px 2px'}}>Total</div>
                {heatmapFirms.map(c => {
                  const firmData = hiringByFirmDept[c.name] || {};
                  const otherCount = otherDepts.reduce((s,d) => s + (firmData[d]||0), 0);
                  const rowTotal = Object.values(firmData).reduce((s,v)=>s+v,0);
                  return [
                    <div key={`${c.name}-dlabel`} style={{fontWeight:600,color:'var(--text)',padding:'4px 2px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',cursor:'pointer'}} onClick={()=>onSelectCompetitor(c)}>
                      {displayName(c.name)}
                    </div>,
                    ...topDepts.map(d => {
                      const val = firmData[d] || 0;
                      const intensity = val > 0 ? Math.max(0.12, val / globalMaxDept) : 0;
                      return (
                        <div key={`${c.name}-${d}`} style={{
                          textAlign:'center',padding:'4px 2px',borderRadius:3,fontFamily:'var(--mono)',fontWeight:600,
                          background: val > 0 ? `rgba(1,23,53,${intensity})` : 'rgba(0,0,0,0.02)',
                          color: val > 0 ? (intensity > 0.5 ? '#fff' : '#3c5b7c') : 'var(--border)',
                        }}>
                          {val || '·'}
                        </div>
                      );
                    }),
                    <div key={`${c.name}-other`} style={{
                      textAlign:'center',padding:'4px 2px',borderRadius:3,fontFamily:'var(--mono)',fontWeight:600,
                      background: otherCount > 0 ? 'rgba(1,23,53,0.08)' : 'rgba(0,0,0,0.02)',
                      color: otherCount > 0 ? '#3c5b7c' : 'var(--border)',
                    }}>
                      {otherCount || '·'}
                    </div>,
                    <div key={`${c.name}-dtotal`} className="mono" style={{textAlign:'center',padding:'4px 2px',fontWeight:700,color:'#3c5b7c',background:'rgba(1,23,53,0.06)',borderRadius:3}}>
                      {rowTotal}
                    </div>
                  ];
                })}
              </div>
            </>
          )}
        </div>

        {/* Top APAC Hirer Card */}
        <div style={{flex:1,background:'linear-gradient(135deg,rgba(1,23,53,0.06),rgba(1,23,53,0.04))',borderRadius:10,padding:14,border:'1px solid rgba(1,23,53,0.1)',display:'flex',flexDirection:'column',justifyContent:'center'}}>
          <div className="section-title">Top APAC Hirer</div>
          <div style={{fontSize:24,fontWeight:700,color:'#3c5b7c',marginBottom:2}}>{topApacHirer?.[0] || '—'}</div>
          <div className="mono" style={{fontSize:30,fontWeight:700,color:'var(--accent)',marginBottom:4}}>{topApacHirer?.[1]?.apac || 0} <span style={{fontSize:14,color:'var(--muted)',fontWeight:400}}>APAC roles</span></div>
          <div style={{fontSize:13,color:'#5e7ea0',lineHeight:1.5,marginBottom:8}}>{topHirerFocusSummary}</div>
          {topHirerDeptsSorted.length > 0 && (
            <div style={{display:'flex',flexDirection:'column',gap:4}}>
              {topHirerDeptsSorted.map(([dept,count],i) => (
                <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:12}}>
                  <span style={{color:'var(--muted)'}}>{dept}</span>
                  <span className="mono" style={{fontWeight:600,color:'var(--accent)'}}>{count}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{fontSize:11,color:'var(--muted)',marginTop:8,borderTop:'1px solid var(--border)',paddingTop:6}}>
            {topApacHirer?.[1]?.total || 0} total roles globally
          </div>
        </div>
      </div>

      {/* APAC Hiring Overview — full width */}
      <div style={{marginBottom:16}}>
        <QuadrantCard title="APAC Hiring Overview" icon="💼" accentColor="var(--accent)">
          <div style={{display:'grid',gridTemplateColumns:'repeat(2, 1fr)',gap:4}}>
            {(() => {
              const maxApac = Math.max(...(competitors||[]).map(c => (jobCounts?.[c.name]?.apac||0)), 1);
              return (competitors||[]).sort((a,b) => (jobCounts?.[b.name]?.apac||0) - (jobCounts?.[a.name]?.apac||0)).map((c,i) => {
                const jc = jobCounts?.[c.name] || {total:0,apac:0};
                const barPct = (jc.apac / maxApac) * 100;
                return (
                  <div key={i} onClick={()=>onSelectCompetitor(c)} style={{
                    display:'flex',alignItems:'center',gap:8,padding:'6px 10px',background:'var(--bg)',borderRadius:6,cursor:'pointer',transition:'all .15s',
                  }}>
                    <span className="mono" style={{width:24,height:24,borderRadius:4,fontSize:10,fontWeight:700,background:'#ebf5fd',color:'var(--muted)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                      {c.logo_url || c.name?.split(' ').map(w=>w[0]).join('').slice(0,2)}
                    </span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:500,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{displayName(c.name)}</div>
                      <div style={{height:3,background:'#ebf5fd',borderRadius:2,marginTop:3}}>
                        <div style={{width:`${barPct}%`,height:'100%',background:'linear-gradient(90deg,var(--accent),var(--accent2))',borderRadius:2,transition:'width .6s ease'}}/>
                      </div>
                    </div>
                    <div style={{textAlign:'right',flexShrink:0}}>
                      <div className="mono" style={{fontSize:14,fontWeight:700,color:'var(--accent)'}}>{jc.apac}</div>
                      <div style={{fontSize:10,color:'var(--muted)'}}>{jc.total} total</div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </QuadrantCard>
      </div>
    </div>
  );
}

function CompetitorDetailPage({ competitor, data, onBack }) {
  const { news, financials, peopleMoves, insights, colour, jobListings } = data;
  const compNews = (news||[]).filter(n => n.competitor_id === competitor.id);
  const compFin = (financials||[]).filter(f => f.competitor_id === competitor.id);
  const compMoves = (peopleMoves||[]).filter(m => m.previous_firm === competitor.name || m.new_firm === competitor.name);
  const compColour = (colour||[]).filter(c => c.competitor_names?.includes(competitor.name));
  const compInsights = (insights||[]).filter(ins => ins.competitor_names?.includes(competitor.name));
  const compJobs = (jobListings||[]).filter(j => j.competitor_id === competitor.id);
  const apacJobs = compJobs.filter(j => isApacLocation(j.location));

  // Location breakdown
  const locBreakdown = {};
  apacJobs.forEach(j => { const l = j.location || 'Unknown'; locBreakdown[l] = (locBreakdown[l]||0) + 1; });
  const locEntries = Object.entries(locBreakdown).sort(([,a],[,b])=>b-a);

  return (
    <div className="fade-up">
      {/* Header */}
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16,padding:'12px 0'}}>
        <button onClick={onBack} className="btn" style={{padding:'4px 10px'}}>← Back</button>
        <div style={{width:40,height:40,borderRadius:8,background:'linear-gradient(135deg,#011735,#ff3300)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,fontWeight:700,color:'#fff'}}>
          {competitor.logo_url || competitor.name?.split(' ').map(w=>w[0]).join('').slice(0,2)}
        </div>
        <div>
          <h2 style={{fontSize:20,fontWeight:700}}>{displayName(competitor.name)}</h2>
          <div style={{fontSize:13,color:'var(--muted)'}}>{competitor.sector} · {competitor.hq} · {competitor.headcount ? `~${competitor.headcount.toLocaleString()} employees` : 'Unknown headcount'}</div>
        </div>
      </div>

      {/* Scrolling News & Intel Ticker */}
      {(() => {
        const feed = [
          ...compNews.map(n => ({ type: 'news', date: n.pub_date, icon: sentColor(n.sentiment), headline: n.headline, sub: `${n.source} · ${fmtDateDMY(n.pub_date)}`, sentiment: n.sentiment, url: n.url })),
          ...compColour.map(c => ({ type: 'colour', date: c.colour_date, icon: '#5e7ea0', headline: c.title, sub: `Intel · ${c.category?.replace(/_/g,' ')} · ${fmtDateDMY(c.colour_date)}` })),
        ].sort((a,b) => new Date(b.date||0) - new Date(a.date||0));
        return feed.length > 0 ? (
          <div className="card" style={{marginBottom:16,padding:'8px 12px'}}>
            <div className="section-title" style={{marginBottom:6}}>Latest News & Intel</div>
            <div style={{maxHeight:100,overflowY:'auto',overflowX:'hidden'}}>
              {feed.slice(0,15).map((f,i) => (
                <div key={i} className="clickable-row" style={{display:'flex',gap:6,alignItems:'center',padding:'3px 0',borderBottom:i<Math.min(feed.length,15)-1?'1px solid var(--border)':'none',cursor:f.url?'pointer':'default',borderRadius:2}} onClick={()=>f.url&&window.open(f.url,'_blank')}>
                  <div style={{width:5,height:5,borderRadius:99,flexShrink:0,background:f.icon}}/>
                  <div style={{fontSize:12,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',flex:1,lineHeight:1.3}}>{f.headline}{f.url && <span style={{fontSize:10,color:'#3c5b7c',marginLeft:4}}>↗</span>}</div>
                  <div style={{fontSize:10,color:'var(--muted)',flexShrink:0,whiteSpace:'nowrap'}}>{fmtDateDMY(f.date)}</div>
                </div>
              ))}
            </div>
          </div>
        ) : null;
      })()}

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
        {/* Financial Data */}
        <div className="card">
          <div className="section-title">Financial Performance</div>
          {(() => {
            const confRank = c => FINANCIAL_CONFIDENCE_RANK[c] || 0;
            const ntiGlobal = selectLatestTradingRevenueObservation(compFin, { region: 'Global' });
            const ntiApac = selectLatestTradingRevenueObservation(compFin, { region: 'APAC' });
            const equityRow = compFin.find(f => f.metric_type === 'total_equity');
            const aumRow = compFin.find(f => f.metric_type === 'aum');
            const equityOrAum = equityRow || aumRow;
            const capDeploy = compFin.find(f => f.metric_type === 'capital_deployed');
            const headcount = competitor.headcount;
            // Metrics already handled above — exclude from "other"
            const handled = new Set([...TRADING_REVENUE_METRICS,'total_equity','aum','capital_deployed','headcount','employee_count','balance_sheet']);
            // Deduplicate: for each remaining metric_type, keep only the best (most recent + most confident)
            const otherMap = {};
            compFin.filter(f => !handled.has(f.metric_type)).forEach(f => {
              const key = `${f.metric_type}|${f.region}`;
              if (!otherMap[key] || (f.period||'') > (otherMap[key].period||'') || confRank(f.confidence) > confRank(otherMap[key].confidence)) otherMap[key] = f;
            });

            const rows = [
              ...(ntiGlobal ? [{ label: `${financialMetricLabel(ntiGlobal)} (Global)`, value: formatFinancialObservationValue(ntiGlobal), sub: `${ntiGlobal.period} · ${financialScopeLabel(ntiGlobal)}`, confidence: ntiGlobal.confidence, color: 'var(--text)', source: ntiGlobal.source, sourceUrl: ntiGlobal.source_url, notes: ntiGlobal.notes, meta: financialObservationMetadata(ntiGlobal) }] : []),
              ...(ntiApac ? [{ label: `${financialMetricLabel(ntiApac)} (APAC)`, value: formatFinancialObservationValue(ntiApac), sub: `${ntiApac.period} · ${financialScopeLabel(ntiApac)}`, confidence: ntiApac.confidence, color: 'var(--text)', source: ntiApac.source, sourceUrl: ntiApac.source_url, notes: ntiApac.notes, meta: financialObservationMetadata(ntiApac) }] : []),
              { label: 'Headcount', value: headcount ? `${headcount.toLocaleString()} ppl` : 'Unknown', sub: 'Global estimated', confidence: headcount ? 'estimated' : null, color: 'var(--text)' },
              { label: equityOrAum ? (equityOrAum.metric_type === 'aum' ? 'AUM' : 'Total Equity') : 'Total Equity / AUM', value: equityOrAum ? formatFinancialObservationValue(equityOrAum) : 'Unknown', sub: equityOrAum ? `${equityOrAum.period} · ${financialScopeLabel(equityOrAum)}` : 'Not publicly disclosed', confidence: equityOrAum?.confidence || null, color: equityOrAum ? 'var(--text)' : 'var(--muted)', source: equityOrAum?.source, sourceUrl: equityOrAum?.source_url, notes: equityOrAum?.notes, meta: financialObservationMetadata(equityOrAum) },
              ...(capDeploy ? [{ label: 'Capital Deployed', value: formatFinancialObservationValue(capDeploy), sub: `${financialScopeLabel(capDeploy)} · ${capDeploy.period}`, confidence: capDeploy.confidence, color: 'var(--text)', source: capDeploy.source, sourceUrl: capDeploy.source_url, notes: capDeploy.notes, meta: financialObservationMetadata(capDeploy) }] : []),
              ...Object.values(otherMap).map(f => ({ label: f.metric_type?.replace(/_/g,' '), value: formatFinancialObservationValue(f), sub: `${financialScopeLabel(f)} · ${f.period}`, confidence: f.confidence, color: 'var(--text)', source: f.source, sourceUrl: f.source_url, notes: f.notes, meta: financialObservationMetadata(f) })),
            ];
            return rows.length === 0 ? <div style={{fontSize:13,color:'var(--muted)',textAlign:'center',padding:12}}>No financial data</div> :
              rows.map((r,i) => {
                const cb = r.confidence ? confBadge(r.confidence) : null;
                return (
                  <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 0',borderBottom:i<rows.length-1?'1px solid var(--border)':'none'}}>
                    <div>
                      <div style={{fontSize:12,fontWeight:500}}>{r.label}</div>
                      <div style={{fontSize:11,color:'var(--muted)'}}>{r.sub}</div>
                      {r.meta && <div style={{fontSize:10,color:'var(--opt-info)',lineHeight:1.4}}>
                        <div>Period bounds: {r.meta.periodBounds} · Metric as-of: {r.meta.metricAsOf} · Source reported: {r.meta.reported}</div>
                        <div>Definition: {r.meta.definition} · Precision: {r.meta.precision} · {r.meta.transformation} · {r.meta.comparability}</div>
                      </div>}
                      {r.sourceUrl ? <a href={r.sourceUrl} target="_blank" rel="noreferrer" style={{fontSize:10,color:'var(--opt-info)'}}>Evidence: {r.source || 'source'} ↗</a> : r.source ? <div style={{fontSize:10,color:'var(--opt-info)'}}>Evidence link unavailable · {r.source}</div> : null}
                      {r.notes && <details style={{fontSize:10,color:'var(--opt-info)'}}><summary style={{cursor:'pointer',color:'var(--opt-info)'}}>Method / caveats</summary><div style={{marginTop:3,maxWidth:520,lineHeight:1.45}}>{r.notes}</div></details>}
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                      <span className="mono" style={{fontSize:14,fontWeight:700,color:r.color}}>{r.value}</span>
                      {cb && <span style={{fontSize:9,padding:'1px 4px',borderRadius:99,background:cb.bg,color:cb.color,fontWeight:600}}>{cb.icon} {cb.label}</span>}
                    </div>
                  </div>
                );
              });
          })()}
        </div>

        {/* APAC Jobs by Location */}
        <div className="card">
          <div className="section-title">APAC Hiring by Location</div>
          {locEntries.length === 0 ? <div style={{fontSize:13,color:'var(--muted)',textAlign:'center',padding:12}}>No APAC jobs</div> :
            locEntries.map(([loc,count],i) => {
              const max = locEntries[0][1];
              return (
                <div key={i} style={{marginBottom:6}}>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:12,marginBottom:2}}>
                    <span>{loc}</span>
                    <span className="mono" style={{fontWeight:600,color:'var(--accent)'}}>{count}</span>
                  </div>
                  <div style={{height:4,background:'#ebf5fd',borderRadius:2}}>
                    <div style={{width:`${(count/max)*100}%`,height:'100%',background:'linear-gradient(90deg,var(--accent),#ff3300)',borderRadius:2}}/>
                  </div>
                </div>
              );
            })
          }
        </div>

        {/* Global Jobs by Department — Pie Chart */}
        <div className="card">
          <div className="section-title">Global Jobs by Department</div>
          {(() => {
            const globalDept = {};
            compJobs.forEach(j => { const d = (j.department === 'Business Operations' || j.department === 'Operations') ? 'Operations' : (j.department || 'Other'); globalDept[d] = (globalDept[d]||0) + 1; });
            const entries = Object.entries(globalDept).sort(([,a],[,b])=>b-a);
            const total = entries.reduce((s,[,v])=>s+v,0);
            if (total === 0) return <div style={{fontSize:13,color:'var(--muted)',textAlign:'center',padding:12}}>No job listings</div>;
            const colors = ['#011735','#ff3300','#3c5b7c','#008753','#5e7ea0','#a855f7','#ec4899','#64748b','#f97316','#6366f1'];
            let cumAngle = 0;
            const slices = entries.map(([dept,count],i) => {
              const pct = count / total;
              const startAngle = cumAngle;
              cumAngle += pct * 360;
              const endAngle = cumAngle;
              const largeArc = pct > 0.5 ? 1 : 0;
              const sr = Math.PI/180;
              const x1 = 50 + 40 * Math.cos((startAngle-90)*sr);
              const y1 = 50 + 40 * Math.sin((startAngle-90)*sr);
              const x2 = 50 + 40 * Math.cos((endAngle-90)*sr);
              const y2 = 50 + 40 * Math.sin((endAngle-90)*sr);
              const path = pct >= 0.999 
                ? `M50,10 A40,40 0 1,1 49.99,10 Z`
                : `M50,50 L${x1},${y1} A40,40 0 ${largeArc},1 ${x2},${y2} Z`;
              return { dept, count, pct, color: colors[i % colors.length], path };
            });
            return (
              <div style={{display:'flex',gap:12,alignItems:'center'}}>
                <svg viewBox="0 0 100 100" width="100" height="100" style={{flexShrink:0}}>
                  {slices.map((s,i) => <path key={i} d={s.path} fill={s.color} stroke="var(--surface)" strokeWidth="0.5"/>)}
                </svg>
                <div style={{flex:1,minWidth:0}}>
                  {entries.slice(0,6).map(([dept,count],i) => (
                    <div key={i} style={{display:'flex',alignItems:'center',gap:5,padding:'2px 0',fontSize:12}}>
                      <div style={{width:6,height:6,borderRadius:1,background:colors[i % colors.length],flexShrink:0}}/>
                      <span style={{flex:1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{dept}</span>
                      <span className="mono" style={{fontWeight:600,color:'var(--muted)',flexShrink:0}}>{count} <span style={{fontSize:10}}>({(count/total*100).toFixed(0)}%)</span></span>
                    </div>
                  ))}
                  {entries.length > 6 && (
                    <div style={{fontSize:11,color:'var(--muted)',marginTop:2}}>+{entries.length - 6} more departments</div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>

        {/* News */}
        <div className="card">
          <div className="section-title">Recent News</div>
          <div style={{maxHeight:240,overflowY:'auto'}}>
            {compNews.length === 0 ? <div style={{fontSize:13,color:'var(--muted)',textAlign:'center',padding:12}}>No news</div> :
              compNews.slice(0,10).map((n,i) => (
                <div key={i} style={{display:'flex',gap:6,alignItems:'flex-start',padding:'5px 0',borderBottom:'1px solid var(--border)'}}>
                  <div style={{width:5,height:5,borderRadius:99,marginTop:5,flexShrink:0,background:sentColor(n.sentiment)}}/>
                  <div>
                    <div style={{fontSize:13,lineHeight:1.35}}>{n.headline}</div>
                    <div style={{fontSize:11,color:'var(--muted)'}}>{n.source} · {fmtDateFull(n.pub_date)}</div>
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      </div>

      {/* People Moves */}
      {compMoves.length > 0 && (
        <div className="card" style={{marginBottom:16}}>
          <div className="section-title">People Moves</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
            {compMoves.map((m,i) => (
              <div key={i} style={{padding:'8px 10px',background:'var(--bg)',borderRadius:6}}>
                <div style={{fontSize:13,fontWeight:600}}>{m.person_name}</div>
                <div style={{fontSize:12,color:'var(--muted)'}}>
                  {m.previous_firm && <><span style={{color:'var(--accent2)'}}>{displayName(m.previous_firm)}</span> → </>}
                  <span style={{color:'var(--green)'}}>{displayName(m.new_firm)}</span>
                </div>
                <div style={{fontSize:11,color:'var(--muted)'}}>{m.new_role} · {m.location} · {fmtDateDMY(m.move_date)}</div>
                {m.impact_analysis && <div style={{fontSize:11,color:'#5e7ea0',marginTop:3,lineHeight:1.35}}>{m.impact_analysis}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Insights + Industry Colour */}
      {(compInsights.length > 0 || compColour.length > 0) && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          {compInsights.length > 0 && (
            <div className="card" style={{borderColor:'rgba(1,23,53,0.15)'}}>
              <div className="section-title">🧠 AI Analysis</div>
              {compInsights.map((ins,i) => (
                <div key={i} style={{marginBottom:8}}>
                  <div style={{fontSize:13,fontWeight:600,marginBottom:3}}>{ins.title}</div>
                  <div style={{fontSize:12,color:'#5e7ea0',lineHeight:1.5}}>{ins.body?.replace(/##?\s/g,'').replace(/\*\*/g,'').slice(0,400)}...</div>
                </div>
              ))}
            </div>
          )}
          {compColour.length > 0 && (
            <div className="card">
              <div className="section-title">🎨 Industry Colour</div>
              {compColour.map((c,i) => (
                <div key={i} style={{padding:'6px 0',borderBottom:i<compColour.length-1?'1px solid var(--border)':'none'}}>
                  <div style={{fontSize:12,fontWeight:600,color:'var(--accent)'}}>{c.title}</div>
                  <div style={{fontSize:12,color:'var(--muted)',lineHeight:1.4,marginTop:2}}>{c.body}</div>
                  <div style={{fontSize:10,color:'var(--muted)',marginTop:2}}>
                    {c.source_type} · {fmtDate(c.colour_date)} · 
                    <span style={{color:c.reliability==='high'?'var(--green)':c.reliability==='low'?'var(--accent2)':'var(--accent)',marginLeft:3}}>{c.reliability} reliability</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function JobListingsTab({ firmEntries, totalApac, totalGlobal, totalFirms }) {
  const [expandAll, setExpandAll] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const limit = expandAll ? 999 : 20;

  // Collect all departments for filter
  const allDepts = {};
  firmEntries.forEach(([,jobs]) => jobs.forEach(j => { if (j.department) allDepts[j.department] = (allDepts[j.department]||0)+1; }));
  const deptOptions = Object.entries(allDepts).sort(([,a],[,b])=>b-a).slice(0,8);

  // Apply search and filter
  const filteredEntries = firmEntries.map(([firm, jobs]) => {
    let fj = jobs;
    if (searchQ) { const q = searchQ.toLowerCase(); fj = fj.filter(j => (j.title||'').toLowerCase().includes(q) || (j.location||'').toLowerCase().includes(q)); }
    if (deptFilter !== 'all') fj = fj.filter(j => j.department === deptFilter);
    return [firm, fj];
  }).filter(([,jobs]) => jobs.length > 0);

  const totalShown = filteredEntries.reduce((s,[,j])=>s+j.length,0);

  return (
    <div>
      <h2 style={{fontSize:18,fontWeight:700,marginBottom:4}}>APAC Job Listings</h2>
      <div style={{fontSize:12,color:'var(--muted)',marginBottom:12}}>{totalApac} active APAC roles across {totalFirms} firms · {totalGlobal} total globally</div>
      
      {/* Controls bar */}
      <div style={{display:'flex',gap:8,marginBottom:12,alignItems:'center',flexWrap:'wrap'}}>
        <input value={searchQ} onChange={e=>setSearchQ(e.target.value)} placeholder="Search roles or locations..."
          style={{padding:'5px 10px',borderRadius:5,border:'1px solid var(--border)',background:'var(--bg)',color:'var(--text)',fontSize:12,fontFamily:'var(--sans)',outline:'none',width:200}} />
        <select value={deptFilter} onChange={e=>setDeptFilter(e.target.value)}
          style={{padding:'5px 8px',borderRadius:5,border:'1px solid var(--border)',background:'var(--bg)',color:'var(--text)',fontSize:12,fontFamily:'var(--sans)',outline:'none'}}>
          <option value="all">All departments</option>
          {deptOptions.map(([d,c]) => <option key={d} value={d}>{d} ({c})</option>)}
        </select>
        <button className={`filter-btn ${expandAll?'filter-btn-active':''}`} onClick={()=>setExpandAll(e=>!e)}>
          {expandAll ? '▼ Collapse' : '▶ Expand all'}
        </button>
        {(searchQ || deptFilter !== 'all') && <span style={{fontSize:11,color:'var(--muted)'}}>{totalShown} results</span>}
      </div>

      {filteredEntries.map(([firm, jobs], fi) => (
        <div key={fi} style={{marginBottom:16}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
            <div style={{display:'flex',alignItems:'center',gap:6}}>
              <span className="mono" style={{width:22,height:22,borderRadius:4,fontSize:10,fontWeight:700,background:'rgba(1,23,53,0.08)',color:'var(--accent)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                {firm.split(' ').map(w=>w[0]).join('').slice(0,2)}
              </span>
              <span style={{fontSize:14,fontWeight:700}}>{firm}</span>
            </div>
            <span className="mono" style={{fontSize:13,fontWeight:600,color:'var(--accent)'}}>{jobs.length} APAC</span>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:4}}>
            {jobs.slice(0,limit).map((j,ji) => (
              <div key={ji} className="clickable-row" style={{padding:'6px 10px',background:'var(--surface)',borderRadius:6,border:'1px solid var(--border)',fontSize:12}}>
                <div style={{fontWeight:500,marginBottom:2}}>{j.title}</div>
                <div style={{display:'flex',gap:6,color:'var(--muted)',fontSize:11}}>
                  <span>{j.location}</span>
                  {j.department && <span style={{padding:'0 4px',borderRadius:2,background:'rgba(1,23,53,0.06)',color:'#3c5b7c'}}>{j.department}</span>}
                </div>
              </div>
            ))}
            {!expandAll && jobs.length > limit && (
              <div style={{padding:'6px 10px',background:'var(--surface)',borderRadius:6,border:'1px solid var(--border)',fontSize:12,color:'var(--muted)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}} onClick={()=>setExpandAll(true)}>
                +{jobs.length - limit} more roles — click to expand
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function IntelDossierPage({ data }) {
  const [activeTab, setActiveTab] = useState('brief');
  const { competitors, news, peopleMoves, insights, colour, jobListings } = data;

  const compName = (id) => { const c = (competitors||[]).find(c=>c.id===id); return c ? displayName(c.name) : '—'; };

  // Junk filter imported from ./config
  const filteredNews = (news||[]).filter(isRelevantNews);
  const briefInsightTypes = new Set(['weekly_analysis', 'weekly_briefing', 'daily_briefing']);
  const isFeaturedInsight = (insight) => briefInsightTypes.has(insight.insight_type)
    || insight.is_pinned
    || insight.priority === 'critical'
    || insight.priority === 'high';

  // Hiring signals: compute per-firm APAC changes / patterns
  const hiringSignals = (competitors||[]).map(comp => {
    const jobs = (jobListings||[]).filter(j => j.competitor_id === comp.id);
    const apacJobs = jobs.filter(j => isApacLocation(j.location));
    if (apacJobs.length === 0) return null;

    // Department breakdown
    const depts = {};
    apacJobs.forEach(j => { const d = j.department || 'Other'; depts[d] = (depts[d]||0)+1; });
    const topDepts = Object.entries(depts).sort(([,a],[,b])=>b-a).slice(0,3);

    // Location breakdown
    const locs = {};
    apacJobs.forEach(j => { const l = groupLoc(j.location); locs[l]=(locs[l]||0)+1; });
    const topLocs = Object.entries(locs).sort(([,a],[,b])=>b-a).slice(0,3);

    // Notable roles (crypto, AI/ML, low latency)
    const notableKeywords = ['crypto','blockchain','ai','machine learning','ml ','fpga','low latency','prediction'];
    const notableRoles = apacJobs.filter(j => notableKeywords.some(k => (j.title||'').toLowerCase().includes(k)));

    return { comp, total: jobs.length, apac: apacJobs.length, topDepts, topLocs, notableRoles };
  }).filter(Boolean).sort((a,b) => b.apac - a.apac);

  const tabs = [
    { id: 'brief', label: '📋 Weekly Brief', icon: '📋' },
    { id: 'news', label: '📰 News', icon: '📰' },
    { id: 'jobs', label: '💼 Job Listings', icon: '💼' },
    { id: 'signals', label: '📡 Hiring Signals', icon: '📡' },
    { id: 'people', label: '🔄 People Moves', icon: '🔄' },
  ];

  return (
    <div className="fade-up gptn-dossier" style={{display:'flex',gap:0,minHeight:'calc(100vh - 120px)'}}>
      {/* Sidebar */}
      <div className="gptn-dossier-sidebar" style={{width:180,flexShrink:0,borderRight:'1px solid var(--border)',paddingTop:8}}>
        <div style={{padding:'0 12px 12px',fontSize:16,fontWeight:700,color:'var(--accent)',letterSpacing:'-0.01em'}}>Intel Dossier</div>
        <div style={{fontSize:11,color:'var(--muted)',padding:'0 12px 12px',lineHeight:1.4}}>
          {new Date().toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric'})}
        </div>
        <div className="gptn-dossier-tabs">
          {tabs.map(t => (
            <button key={t.id} onClick={()=>setActiveTab(t.id)} className={`gptn-dossier-tab ${activeTab===t.id?'gptn-dossier-tab-active':''}`} style={{
              display:'flex',alignItems:'center',gap:8,width:'100%',padding:'9px 12px',border:'none',cursor:'pointer',
              fontFamily:'var(--sans)',fontSize:13,fontWeight:activeTab===t.id?600:400,textAlign:'left',
              background:activeTab===t.id?'rgba(1,23,53,0.05)':'transparent',
              color:activeTab===t.id?'var(--accent)':'var(--muted)',
              borderLeft:activeTab===t.id?'2px solid var(--accent)':'2px solid transparent',
              transition:'all .15s',
            }}>
              <span style={{fontSize:15}}>{t.icon}</span>{t.label.split(' ').slice(1).join(' ')}
            </button>
          ))}
        </div>
        {/* Summary counts */}
        <div className="gptn-dossier-summary" style={{padding:'16px 12px',borderTop:'1px solid var(--border)',marginTop:8}}>
          <div style={{fontSize:10,color:'var(--muted)',letterSpacing:'.06em',textTransform:'uppercase',marginBottom:6}}>Current Intel</div>
          {[
            { label: 'News Items', val: filteredNews.length, color: '#3c5b7c' },
            { label: 'People Moves', val: (peopleMoves||[]).length, color: 'var(--accent2)' },
            { label: 'Active Jobs', val: (jobListings||[]).length, color: 'var(--accent)' },
            { label: 'AI Insights', val: (insights||[]).length, color: 'var(--green)' },
            { label: 'Colour Items', val: (colour||[]).length, color: '#5e7ea0' },
          ].map((s,i) => (
            <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'3px 0',fontSize:12}}>
              <span style={{color:'var(--muted)'}}>{s.label}</span>
              <span className="mono" style={{fontWeight:600,color:s.color}}>{s.val}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="gptn-dossier-content" style={{flex:1,padding:'12px 20px',overflowY:'auto',maxHeight:'calc(100vh - 120px)'}}>

        {/* ── WEEKLY BRIEF TAB ── */}
        {activeTab === 'brief' && (
          <div>
            <h2 style={{fontSize:18,fontWeight:700,marginBottom:4}}>Weekly Intelligence Brief</h2>
            <div style={{fontSize:12,color:'var(--muted)',marginBottom:16}}>Validated weekly strategic summary across all tracked competitors</div>
            {(insights||[]).length === 0 ? (
              <div className="card" style={{textAlign:'center',color:'var(--muted)',fontSize:14,padding:30}}>No weekly briefs yet — add a reviewed ai_insights record with insight_type "weekly_analysis"</div>
            ) : (
              (insights||[]).filter(isFeaturedInsight).map((ins,i) => (
                <div key={i} className="card" style={{marginBottom:12,borderColor:'rgba(1,23,53,0.12)',background:'linear-gradient(135deg,rgba(1,23,53,0.02),rgba(255,51,0,0.01))'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                    <span style={{fontSize:14,fontWeight:700}}>{ins.title}</span>
                    <div style={{display:'flex',gap:4}}>
                      {ins.priority && <span style={{fontSize:10,padding:'2px 6px',borderRadius:99,fontWeight:700,background:ins.priority==='critical'?'rgba(255,51,0,0.1)':'rgba(1,23,53,0.12)',color:ins.priority==='critical'?'var(--accent2)':'var(--accent)'}}>{ins.priority}</span>}
                      <span style={{fontSize:10,padding:'2px 6px',borderRadius:99,background:'rgba(1,23,53,0.08)',color:'#3c5b7c',fontWeight:600}}>{ins.insight_type?.replace(/_/g,' ')}</span>
                    </div>
                  </div>
                  <div style={{fontSize:13,color:'#3c5b7c',lineHeight:1.65,whiteSpace:'pre-wrap'}}>
                    {(ins.body||'').replace(/##\s*/g,'').replace(/\*\*/g,'').replace(/\*/g,'')}
                  </div>
                  <div style={{display:'flex',gap:8,marginTop:10,flexWrap:'wrap'}}>
                    {(ins.competitor_names||[]).map((cn,ci) => (
                      <span key={ci} style={{fontSize:10,padding:'2px 6px',borderRadius:4,background:'rgba(0,0,0,0.03)',color:'var(--muted)',border:'1px solid var(--border)'}}>{cn}</span>
                    ))}
                  </div>
                  <div style={{fontSize:10,color:'var(--muted)',marginTop:6}}>{fmtDateFull(ins.generated_date)} · {ins.region}</div>
                </div>
              ))
            )}
            {/* Also show non-briefing insights */}
            {(insights||[]).filter(i => !isFeaturedInsight(i)).length > 0 && (
              <>
                <div className="section-title" style={{marginTop:12}}>Other AI Analysis</div>
                {(insights||[]).filter(i => !isFeaturedInsight(i)).map((ins,i) => (
                  <div key={i} className="card" style={{marginBottom:8,padding:12}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                      <span style={{fontSize:13,fontWeight:600}}>{ins.title}</span>
                      <span style={{fontSize:10,padding:'1px 5px',borderRadius:99,background:'rgba(1,23,53,0.08)',color:'#3c5b7c',fontWeight:600}}>{ins.insight_type?.replace(/_/g,' ')}</span>
                    </div>
                    <div style={{fontSize:12,color:'#5e7ea0',lineHeight:1.5}}>{ins.body?.replace(/##?\s/g,'').replace(/\*\*/g,'').slice(0,300)}...</div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* ── NEWS TAB ── */}
        {activeTab === 'news' && (
          <div>
            <h2 style={{fontSize:18,fontWeight:700,marginBottom:4}}>News Feed</h2>
            <div style={{fontSize:12,color:'var(--muted)',marginBottom:16}}>{filteredNews.length} relevant articles across all firms</div>
            {filteredNews.length === 0 ? (
              <div className="card" style={{textAlign:'center',color:'var(--muted)',fontSize:14,padding:30}}>No news items</div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:6}}>
                {filteredNews.map((n,i) => (
                  <div key={i} className="card" style={{padding:10,display:'flex',gap:10,alignItems:'flex-start',animation:`slideIn .3s ease forwards`,animationDelay:`${Math.min(i*0.03,0.5)}s`,opacity:0}}>
                    <div style={{width:6,height:6,borderRadius:99,marginTop:6,flexShrink:0,background:sentColor(n.sentiment)}}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:500,lineHeight:1.4,marginBottom:3}}>
                        {n.url ? <a href={n.url} target="_blank" rel="noopener noreferrer" style={{color:'var(--text)',textDecoration:'none'}}>{n.headline}</a> : n.headline}
                      </div>
                      <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
                        <span style={{fontSize:11,padding:'1px 5px',borderRadius:3,background:'rgba(1,23,53,0.08)',color:'var(--accent)',fontWeight:600}}>{compName(n.competitor_id)}</span>
                        {n.category && n.category !== 'general' && <span style={{fontSize:10,padding:'1px 4px',borderRadius:3,background:'rgba(1,23,53,0.08)',color:'#3c5b7c',fontWeight:600}}>{n.category}</span>}
                        <span style={{fontSize:11,color:'var(--muted)'}}>{n.source} · {fmtDateFull(n.pub_date)}</span>
                        {n.region && <span style={{fontSize:10,color:'var(--muted)'}}>{n.region}</span>}
                      </div>
                      {n.summary && <div style={{fontSize:12,color:'#5e7ea0',lineHeight:1.4,marginTop:4}}>{n.summary}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── JOB LISTINGS TAB ── */}
        {activeTab === 'jobs' && (() => {
          const apacJobs = (jobListings||[]).filter(j => isApacLocation(j.location));
          // Group by firm
          const jobsByFirm = {};
          apacJobs.forEach(j => {
            const cn = compName(j.competitor_id);
            if (!jobsByFirm[cn]) jobsByFirm[cn] = [];
            jobsByFirm[cn].push(j);
          });
          const firmEntries = Object.entries(jobsByFirm).sort(([,a],[,b]) => b.length - a.length);

          return (
            <JobListingsTab firmEntries={firmEntries} totalApac={apacJobs.length} totalGlobal={(jobListings||[]).length} totalFirms={firmEntries.length} />
          );
        })()}

        {/* ── HIRING SIGNALS TAB ── */}
        {activeTab === 'signals' && (
          <div>
            <h2 style={{fontSize:18,fontWeight:700,marginBottom:4}}>Hiring Signals</h2>
            <div style={{fontSize:12,color:'var(--muted)',marginBottom:16}}>Aggregated patterns and strategic signals from APAC job data</div>
            {hiringSignals.length === 0 ? (
              <div className="card" style={{textAlign:'center',color:'var(--muted)',fontSize:14,padding:30}}>No hiring signals detected</div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:10}}>
                {hiringSignals.map((sig,i) => (
                  <div key={i} className="card" style={{padding:14}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <span className="mono" style={{width:28,height:28,borderRadius:6,fontSize:11,fontWeight:700,background:'linear-gradient(135deg,rgba(1,23,53,0.15),rgba(255,51,0,0.08))',color:'var(--accent)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                          {sig.comp.name.split(' ').map(w=>w[0]).join('').slice(0,2)}
                        </span>
                        <div>
                          <div style={{fontSize:15,fontWeight:700}}>{displayName(sig.comp.name)}</div>
                          <div style={{fontSize:11,color:'var(--muted)'}}>{sig.comp.hq} · {sig.comp.sector}</div>
                        </div>
                      </div>
                      <div style={{textAlign:'right'}}>
                        <div className="mono" style={{fontSize:20,fontWeight:700,color:'var(--accent)'}}>{sig.apac}</div>
                        <div style={{fontSize:10,color:'var(--muted)'}}>APAC roles / {sig.total} total</div>
                      </div>
                    </div>

                    <div style={{display:'flex',gap:16,marginBottom:8}}>
                      {/* Top locations */}
                      <div style={{flex:1}}>
                        <div style={{fontSize:10,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Top Locations</div>
                        {sig.topLocs.map(([loc,cnt],li) => (
                          <div key={li} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'2px 0',fontSize:12}}>
                            <span>{loc}</span>
                            <span className="mono" style={{fontWeight:600,color:'var(--accent)'}}>{cnt}</span>
                          </div>
                        ))}
                      </div>
                      {/* Top departments */}
                      <div style={{flex:1}}>
                        <div style={{fontSize:10,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>Top Departments</div>
                        {sig.topDepts.map(([dept,cnt],di) => (
                          <div key={di} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'2px 0',fontSize:12}}>
                            <span>{dept}</span>
                            <span className="mono" style={{fontWeight:600,color:'#3c5b7c'}}>{cnt}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Notable roles */}
                    {sig.notableRoles.length > 0 && (
                      <div style={{borderTop:'1px solid var(--border)',paddingTop:6}}>
                        <div style={{fontSize:10,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4}}>🔥 Notable Roles (Crypto / AI / Low Latency / Prediction Markets)</div>
                        <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                          {sig.notableRoles.slice(0,8).map((r,ri) => (
                            <span key={ri} style={{fontSize:11,padding:'2px 6px',borderRadius:4,background:'rgba(255,51,0,0.06)',color:'var(--accent2)',border:'1px solid rgba(255,51,0,0.1)'}}>
                              {r.title} — {APAC_LOCATIONS.find(a=>r.location?.includes(a))||r.location}
                            </span>
                          ))}
                          {sig.notableRoles.length > 8 && <span style={{fontSize:11,color:'var(--muted)',padding:'2px 6px'}}>+{sig.notableRoles.length - 8} more</span>}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Industry colour related to hiring */}
            {(colour||[]).filter(c=>c.category==='people_moves'||c.tags?.some(t=>['hiring','talent','apac'].includes(t))).length > 0 && (
              <div style={{marginTop:16}}>
                <div className="section-title">Related Intelligence</div>
                {(colour||[]).filter(c=>c.category==='people_moves'||c.tags?.some(t=>['hiring','talent','apac'].includes(t))).map((c,i) => (
                  <div key={i} className="card" style={{marginBottom:6,padding:10,borderLeft:'2px solid var(--accent)'}}>
                    <div style={{fontSize:12,fontWeight:600,color:'var(--accent)',marginBottom:2}}>{c.title}</div>
                    <div style={{fontSize:12,color:'#5e7ea0',lineHeight:1.4}}>{c.body}</div>
                    <div style={{fontSize:10,color:'var(--muted)',marginTop:3}}>{c.source_type} · {fmtDate(c.colour_date)} · {c.reliability} reliability</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── PEOPLE MOVES TAB ── */}
        {activeTab === 'people' && (
          <div>
            <h2 style={{fontSize:18,fontWeight:700,marginBottom:4}}>People Moves</h2>
            <div style={{fontSize:12,color:'var(--muted)',marginBottom:16}}>{(peopleMoves||[]).length} tracked moves across all firms</div>
            {(peopleMoves||[]).length === 0 ? (
              <div className="card" style={{textAlign:'center',color:'var(--muted)',fontSize:14,padding:30}}>No people moves recorded yet</div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {(peopleMoves||[]).map((m,i) => (
                  <div key={i} className="card" style={{padding:12,animation:`slideIn .3s ease forwards`,animationDelay:`${Math.min(i*0.04,0.5)}s`,opacity:0}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                      <div style={{flex:1}}>
                        <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
                          <div style={{width:28,height:28,borderRadius:99,background:'linear-gradient(135deg,rgba(255,51,0,0.08),rgba(1,23,53,0.08))',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,color:'var(--accent)'}}>
                            {(m.person_name||'?')[0]}
                          </div>
                          <div>
                            <div style={{fontSize:14,fontWeight:700}}>{m.person_name}</div>
                            <div style={{fontSize:11,color:'var(--muted)'}}>{m.seniority} · {m.asset_class || 'N/A'} · {m.location}</div>
                          </div>
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4,marginLeft:34}}>
                          {m.previous_firm && (
                            <span style={{fontSize:12,padding:'2px 8px',borderRadius:4,background:'rgba(255,51,0,0.06)',color:'var(--accent2)',fontWeight:500}}>
                              {displayName(m.previous_firm)} — {m.previous_role || '?'}
                            </span>
                          )}
                          <span style={{fontSize:14,color:'var(--muted)'}}>→</span>
                          <span style={{fontSize:12,padding:'2px 8px',borderRadius:4,background:'rgba(34,197,94,0.08)',color:'var(--green)',fontWeight:500}}>
                            {displayName(m.new_firm) || '?'} — {m.new_role || '?'}
                          </span>
                        </div>
                        {m.impact_analysis && (
                          <div style={{fontSize:12,color:'#5e7ea0',lineHeight:1.45,marginLeft:34,marginTop:4,padding:'6px 10px',background:'rgba(1,23,53,0.03)',borderRadius:6,borderLeft:'2px solid var(--accent)'}}>
                            <span style={{fontSize:10,fontWeight:600,color:'var(--accent)',marginRight:4}}>IMPACT:</span>{m.impact_analysis}
                          </div>
                        )}
                      </div>
                      <div style={{textAlign:'right',flexShrink:0,marginLeft:12}}>
                        <div style={{fontSize:11,color:'var(--muted)'}}>{fmtDateDMY(m.move_date || m.announced_date)}</div>
                        <div style={{display:'flex',gap:3,justifyContent:'flex-end',marginTop:3}}>
                          {m.is_confirmed && <span style={{fontSize:9,padding:'1px 4px',borderRadius:99,background:'rgba(0,135,83,0.1)',color:'#008753',fontWeight:700}}>Confirmed</span>}
                          {m.move_type && <span style={{fontSize:9,padding:'1px 4px',borderRadius:99,background:'rgba(1,23,53,0.08)',color:'#3c5b7c',fontWeight:600}}>{m.move_type.replace(/_/g,' ')}</span>}
                          {m.region && <span style={{fontSize:9,padding:'1px 4px',borderRadius:99,background:'rgba(0,0,0,0.03)',color:'var(--muted)'}}>{m.region}</span>}
                        </div>
                        <div style={{fontSize:10,color:'var(--muted)',marginTop:3}}>via {m.source || '—'}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FeedbackPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState('comment');
  const [submitted, setSubmitted] = useState(false);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeedback();
  }, []);

  const loadFeedback = async () => {
    try {
      const data = await sbQuery('feedback', { order: 'created_at.desc', limit: 50 });
      setFeedbacks(data || []);
    } catch { setFeedbacks([]); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!message.trim()) return;
    const ok = await sbInsert('feedback', { author_name: name || 'Anonymous', message, feedback_type: feedbackType });
    if (ok) { setSubmitted(true); setMessage(''); setName(''); setEmail(''); setTimeout(() => { setSubmitted(false); loadFeedback(); }, 3000); }
  };

  return (
    <div className="fade-up" style={{maxWidth:800,margin:'0 auto'}}>
      <h2 style={{fontSize:22,fontWeight:700,marginBottom:4}}>Feedback & Questions</h2>
      <p style={{fontSize:14,color:'var(--muted)',marginBottom:20}}>Share your thoughts, ask questions, or suggest improvements. Your feedback helps make GPTN better.</p>

      {/* Submit Form */}
      <div className="card" style={{marginBottom:20}}>
        {submitted ? (
          <div style={{textAlign:'center',padding:'30px 20px'}}>
            <div style={{fontSize:30,marginBottom:8}}>✅</div>
            <div style={{fontSize:16,fontWeight:700,color:'var(--green)',marginBottom:4}}>Thank you for your feedback!</div>
            <div style={{fontSize:13,color:'var(--muted)'}}>Your {feedbackType} has been recorded and will be reviewed by the GPTN team.</div>
          </div>
        ) : (
          <>
            <div className="section-title">Submit Feedback</div>
            <div style={{display:'flex',gap:8,marginBottom:10}}>
              {['comment','question','suggestion','bug'].map(t => (
                <button key={t} className={`btn ${feedbackType===t?'btn-active':''}`} onClick={()=>setFeedbackType(t)} style={{fontSize:12,padding:'4px 10px',textTransform:'capitalize'}}>{t}</button>
              ))}
            </div>
            <div style={{display:'flex',gap:8,marginBottom:8}}>
              <input value={name} onChange={e=>setName(e.target.value)} placeholder="Your name (optional)"
                style={{flex:1,padding:'8px 12px',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg)',color:'var(--text)',fontSize:14,fontFamily:'var(--sans)',outline:'none'}}/>
              <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email for follow-up (optional)"
                style={{flex:1,padding:'8px 12px',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg)',color:'var(--text)',fontSize:14,fontFamily:'var(--sans)',outline:'none'}}/>
            </div>
            <textarea value={message} onChange={e=>setMessage(e.target.value)} placeholder="Write your feedback or question here..."
              rows={4} style={{width:'100%',padding:'8px 12px',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg)',color:'var(--text)',fontSize:14,fontFamily:'var(--sans)',marginBottom:8,outline:'none',resize:'vertical'}}/>
            <div style={{display:'flex',justifyContent:'flex-end'}}>
              <button onClick={handleSubmit} className="btn btn-active" disabled={!message.trim()} style={{padding:'6px 20px',opacity:message.trim()?1:0.5}}>Submit</button>
            </div>
          </>
        )}
      </div>

      {/* Existing Feedback */}
      <div className="section-title">Recent Feedback</div>
      {loading ? <div style={{fontSize:13,color:'var(--muted)',textAlign:'center',padding:20}}>Loading...</div> :
        feedbacks.length === 0 ? <div className="card" style={{textAlign:'center',color:'var(--muted)',fontSize:14,padding:20}}>No feedback yet. Be the first!</div> :
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {feedbacks.map((f,i) => (
            <div key={i} className="card" style={{padding:12}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  <div style={{width:24,height:24,borderRadius:99,background:'#ebf5fd',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:600,color:'var(--accent)'}}>
                    {(f.author_name||'A')[0].toUpperCase()}
                  </div>
                  <span style={{fontSize:13,fontWeight:600}}>{f.author_name || 'Anonymous'}</span>
                  <span style={{fontSize:10,padding:'1px 5px',borderRadius:99,background:'rgba(1,23,53,0.08)',color:'#3c5b7c',fontWeight:600}}>{f.feedback_type}</span>
                </div>
                <span style={{fontSize:11,color:'var(--muted)'}}>{fmtDateFull(f.created_at)}</span>
              </div>
              <div style={{fontSize:13,lineHeight:1.5,color:'#5e7ea0'}}>{f.message}</div>
              {f.admin_response && (
                <div style={{marginTop:8,padding:'6px 10px',background:'rgba(1,23,53,0.04)',borderRadius:6,borderLeft:'2px solid var(--accent)'}}>
                  <div style={{fontSize:11,fontWeight:600,color:'var(--accent)',marginBottom:2}}>GPTN Team Response</div>
                  <div style={{fontSize:12,color:'var(--text)',lineHeight:1.4}}>{f.admin_response}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      }
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// MAIN APP
// ═════════════════════════════════════════════════════════════

function PasswordGate({ onUnlock }) {
  const [pw, setPw] = useState('');
  const [error, setError] = useState(false);
  const handleSubmit = () => {
    if (pw === ACCESS_PASSWORD) onUnlock();
    else { setError(true); setTimeout(() => setError(false), 1500); }
  };
  return (
    <div className="gptn-password-gate opt-dark" style={{minHeight:'100vh',background:'var(--bg)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}}>
      <style>{CSS}</style>
      <div className="gptn-access-title" style={{marginBottom:8}}>
        GPTN <em>intelligence.</em>
      </div>
      <div className="gptn-access-subtitle" style={{fontSize:13,color:'var(--muted)',marginBottom:24}}>Global Prop Trading News · Automated Weekly Updates Based On Public Data Only</div>
      <div style={{display:'flex',gap:8}}>
        <input aria-label="Access code" type="password" value={pw} onChange={e=>setPw(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleSubmit()}
          placeholder="Access code" style={{padding:'8px 14px',borderRadius:8,border:`1px solid ${error?'var(--accent2)':'var(--border)'}`,background:'var(--surface)',color:'var(--text)',fontSize:15,fontFamily:'var(--sans)',outline:'none'}}/>
        <button onClick={handleSubmit} className="btn btn-active">Enter</button>
      </div>
      {error && <div style={{fontSize:13,color:'var(--accent2)',marginTop:8}}>Incorrect</div>}
      <div className="gptn-access-disclaimer" style={{marginTop:32,width:'calc(100% - 40px)',maxWidth:540}}>
        <DisclaimerBanner />
      </div>
    </div>
  );
}

export default function App() {
  // Design-system showcase bypass: ?theme=1 or #theme renders the
  // ThemeShowcase page without touching the password-gated dashboard.
  // Wrapper (not an early return inside DashboardApp) keeps hooks lint-safe.
  if (window.location.search.includes('theme=1') || window.location.hash.includes('theme')) {
    return <ThemeShowcase />;
  }
  return <DashboardApp />;
}

function DashboardApp() {
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(() => new URLSearchParams(window.location.search).get('page') === 'oct-26-case-study' ? 'oct26' : 'home');
  const [selectedComp, setSelectedComp] = useState(null);
  const [data, setData] = useState({});

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [competitors, legacyFinancials, peopleMoves, news, insights, colour, jobListings, performanceIntelligence] = await Promise.all([
        sbQuery('competitors', { order: 'name' }),
        sbQuery('competitor_financials', { select: '*,competitors(name)', order: 'reported_date.desc' }),
        sbQuery('people_moves', { order: 'announced_date.desc', limit: 50 }),
        sbQuery('news_items', { order: 'pub_date.desc', limit: 100 }),
        sbQuery('ai_insights', { order: 'generated_date.desc', limit: 20 }),
        sbQuery('industry_colour', { order: 'colour_date.desc', limit: 30 }),
        sbQuery('job_listings', { filter: 'status=eq.active', order: 'first_seen_date.desc', limit: 5000 }),
        sbQueryOptional('performance_intelligence_v1', { order: 'period_end.desc' }),
      ]);

      // Existing product screens continue to use the legacy source table. The
      // chart alone reads the finance-reviewed compatibility publication view.
      const financials = legacyFinancials;
      const performanceObservations = (performanceIntelligence || []).filter(row => row.record_type === 'observation');
      const financialScenarios = (performanceIntelligence || []).filter(row => row.record_type === 'outlook_scenario');

      // Compute job counts per competitor
      const jobCounts = {};
      (competitors||[]).forEach(c => { jobCounts[c.name] = {total:0, apac:0}; });
      (jobListings||[]).forEach(j => {
        const comp = (competitors||[]).find(c => c.id === j.competitor_id);
        if (comp && jobCounts[comp.name]) {
          jobCounts[comp.name].total++;
          if (isApacLocation(j.location)) jobCounts[comp.name].apac++;
        }
      });

      // Attach competitor_name to financials
      const enrichedFinancials = (financials||[]).map(f => ({
        ...f,
        competitor_name: f.competitors?.name || (competitors||[]).find(c=>c.id===f.competitor_id)?.name || '—'
      }));

      // Compute most recent data point timestamp across key tables
      const allDates = [
        ...(news||[]).map(n=>n.pub_date),
        ...(peopleMoves||[]).map(m=>m.announced_date||m.move_date),
        ...(jobListings||[]).map(j=>j.first_seen_date),
      ].filter(Boolean).map(d=>new Date(d)).sort((a,b)=>b-a);
      const lastUpdated = allDates[0]?.toISOString() || null;

      setData({ competitors, financials: enrichedFinancials, performanceObservations, financialScenarios, peopleMoves, news, insights, colour, jobListings, jobCounts, lastUpdated });
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (authed) fetchAll(); }, [authed, fetchAll]);

  useEffect(() => {
    const activeTab = document.querySelector('.gptn-nav [aria-current="page"]');
    activeTab?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [page, authed]);

  // Dynamic page title
  useEffect(() => {
    const titles = { home: 'Dashboard', competitor: selectedComp?.name || 'Competitor', dossier: 'Insights', analysis: 'Mar-2026 Ad Hoc Analysis', oct26: 'Oct-26 Case study — RWA', feedback: 'Feedback' };
    document.title = `GPTN — ${titles[page] || 'Global Prop Trading News'}`;
    const url = new URL(window.location.href);
    if (page === 'oct26') url.searchParams.set('page', 'oct-26-case-study');
    else if (url.searchParams.get('page') === 'oct-26-case-study') url.searchParams.delete('page');
    window.history.replaceState({}, '', url);
  }, [page, selectedComp]);

  if (!authed) return <PasswordGate onUnlock={() => setAuthed(true)} />;

  return (
    <div className="gptn-shell" style={{minHeight:'100vh',background:'var(--bg)',color:'var(--text)',fontFamily:'var(--sans)'}}>
      <style>{CSS}</style>

      {/* Navigation */}
      <header className="gptn-header" style={{borderBottom:'1px solid var(--border)',padding:'0 20px',display:'flex',justifyContent:'space-between',alignItems:'center',height:52}}>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <div style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer'}} onClick={()=>{setPage('home');setSelectedComp(null);}}>
            <span className="gptn-brand-mark" aria-hidden="true"><i/><i/><i/></span>
            <div>
              <span className="gptn-brand-name" style={{fontSize:17,fontWeight:700,letterSpacing:'.08em'}}>GPTN</span>
              <span className="gptn-brand-subtitle" style={{fontSize:11,color:'var(--muted)',marginLeft:8}}>Global Prop Trading News</span>
            </div>
          </div>
          <span className="gptn-live" style={{fontSize:9,padding:'3px 7px',borderRadius:99,background:'rgba(0,135,83,0.1)',color:'var(--green)',fontWeight:700,letterSpacing:'.08em'}}>LIVE</span>
        </div>
        <nav className="gptn-nav" aria-label="Primary navigation" style={{display:'flex',gap:4,alignItems:'center'}}>
          {[
            {id:'home',label:'Dashboard'},
            {id:'dossier',label:'Insights'},
            {id:'analysis',label:'Mar-2026 Ad Hoc Analysis'},
            {id:'oct26',label:'Oct-26 Case study'},
            {id:'feedback',label:'Feedback'},
          ].map(tab => (
            <button key={tab.id} onClick={()=>{setPage(tab.id);if(tab.id!=='competitor')setSelectedComp(null);}}
              aria-current={page===tab.id?'page':undefined}
              className={`btn ${page===tab.id?'btn-active':''}`}>{tab.label}</button>
          ))}
          <button onClick={fetchAll} className="btn gptn-refresh" aria-label="Refresh intelligence data" style={{marginLeft:4}}>↻</button>
        </nav>
      </header>

      {/* Content */}
      <main className="gptn-main" style={{maxWidth:1320,margin:'0 auto',padding:'18px 20px'}}>
        {page === 'oct26' ? (
          <Oct2026CaseStudy />
        ) : loading ? (
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minHeight:400}}>
            <div style={{width:36,height:36,border:'3px solid var(--border)',borderTop:'3px solid var(--accent)',borderRadius:'50%',animation:'spin .8s linear infinite',marginBottom:14}}/>
            <style>{`@keyframes spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}`}</style>
            <div style={{fontSize:15,fontWeight:600,animation:'pulse 1.5s ease infinite'}}>Loading intelligence data...</div>
          </div>
        ) : error ? (
          <div style={{textAlign:'center',padding:40}}>
            <div style={{fontSize:18,fontWeight:600,marginBottom:8}}>Failed to load data</div>
            <div style={{fontSize:14,color:'var(--muted)',marginBottom:16}}>{error}</div>
            <button onClick={fetchAll} className="btn btn-active">Retry</button>
          </div>
        ) : page === 'home' ? (
          <HomePage data={data} onSelectCompetitor={(c)=>{setSelectedComp(c);setPage('competitor');}}/>
        ) : page === 'competitor' && selectedComp ? (
          <CompetitorDetailPage competitor={selectedComp} data={data} onBack={()=>{setPage('home');setSelectedComp(null);}}/>
        ) : page === 'dossier' ? (
          <IntelDossierPage data={data} />
        ) : page === 'analysis' ? (
          <CitadelAnalysisPage />
        ) : page === 'feedback' ? (
          <FeedbackPage />
        ) : (
          <HomePage data={data} onSelectCompetitor={(c)=>{setSelectedComp(c);setPage('competitor');}}/>
        )}
      </main>

      {/* Footer */}
      <footer className="gptn-footer" style={{borderTop:'1px solid var(--border)',padding:'16px 20px',textAlign:'center'}}>
        <div style={{fontSize:11,color:'var(--muted)'}}>
          GPTN · Global Prop Trading News · APAC Focus · {data.competitors?.length||0} firms · ~{Object.values(data.jobCounts||{}).reduce((s,v)=>s+v.total,0)} jobs tracked
          {data.lastUpdated && <span> · Last refreshed: {new Date(data.lastUpdated).toLocaleDateString('en-US',{day:'numeric',month:'short',year:'numeric'})}</span>}
        </div>
      </footer>
    </div>
  );
}
